import React, { useState, useMemo, useCallback, useEffect } from "react";
import * as XLSX from "xlsx";

const C = { 
  bg: "#020617",      
  card: "#0f172a",    
  border: "#1e293b",  
  text: "#f8fafc",    
  muted: "#94a3b8",   
  accent: "#38bdf8",  
  pos: "#10b981",     
  neg: "#ef4444",     
  warn: "#f59e0b",    
  glass: "rgba(255, 255, 255, 0.03)" 
};
const F = { width:"100%",padding:"6px 10px",background:"#0a0f1e",border:"1px solid #1f2937",borderRadius:6,color:"#f9fafb",fontFamily:"inherit",fontSize:11,boxSizing:"border-box" };
const fmt = (n) => { if (n==null||isNaN(n)) return "—"; const a=Math.abs(n).toLocaleString("en-US",{maximumFractionDigits:0}); return n<0?`(${a})`:a; };

function formatExcelDate(v) {
  if (!v) return "";
  let s = String(v).trim();
  if (/^\d{5}$/.test(s)) {
    const serial = parseInt(s, 10);
    if (serial > 40000 && serial < 60000) {
      const d = new Date(Math.round((serial - 25569) * 86400 * 1000));
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${String(d.getUTCDate()).padStart(2, '0')}-${months[d.getUTCMonth()]}`;
    }
  }
  return s;
}
  
function buildLegs(netData, mapping, dates, fxRates) { const fx = c => fxRates[c] || 1; const legs = {}; const accountToLeg = {}; netData.forEach(acct => { const m = mapping.find(x => x.sub.toUpperCase().trim() === acct.name.toUpperCase().trim()); const cur = (acct.cur || m?.ccy || "EUR").toUpperCase(); const pool = (m?.pool && m.pool.trim()) ? m.pool.trim() : (m?.master || acct.name).trim(); const master = (m?.master || acct.name).trim().toUpperCase(); const isStandalone = !(m && m.pool && m.pool.trim()); const key = cur + "||" + pool; if (!legs[key]) legs[key] = { key, cur, pool, master, isStandalone, subs: [], balances: dates.map(() => 0) }; accountToLeg[acct.name] = key; legs[key].subs.push(acct.name); (acct.values || []).forEach((v, i) => { if (legs[key].balances[i] !== undefined) legs[key].balances[i] += v; }); }); const list = Object.values(legs).map(l => ({ ...l, eur: l.balances.map(v => v / fx(l.cur)) })); return { legs: list, accountToLeg }; }

// Facility Opening / Net movements / Expected (EUR) derived from the app's account balances
// (OD_Struktur "Cash Pool Master" = facility). Kredit facilitet and other rows are kept as read.
function deriveFacilityRows(netData, mapping, fxRates, dates, facRows) {
  const fx = c => fxRates[c] || 1;
  const byFac = {};
  [...new Set(facRows.map(r => r.fac.toUpperCase().trim()))].forEach(k => { byFac[k] = dates.map(() => 0); });
  netData.forEach(acct => {
    const m = mapping.find(x => x.sub.toUpperCase().trim() === acct.name.toUpperCase().trim());
    const k = (m?.master || "").toUpperCase().trim();
    if (!byFac[k]) return;
    const cur = (acct.cur || m?.ccy || "EUR").toUpperCase();
    (acct.values || []).forEach((v, i) => { if (byFac[k][i] !== undefined) byFac[k][i] += v / fx(cur); });
  });
  const rowsFor = k => {
    const exp = byFac[k];
    return {
      "OPENING": dates.map(() => exp[0] ?? 0),
      "NET MOVEMENTS": exp.map((v, i) => i === 0 ? 0 : v - exp[i - 1]),
      "EXPECTED": exp
    };
  };
  const out = facRows.map(r => {
    const k = r.fac.toUpperCase().trim(), t = r.type.toUpperCase().trim();
    return (byFac[k] && ["OPENING", "NET MOVEMENTS", "EXPECTED"].includes(t)) ? { fac: r.fac, type: r.type, values: rowsFor(k)[t] } : r;
  });
  const facName = {};
  facRows.forEach(r => { facName[r.fac.toUpperCase().trim()] = r.fac; });
  Object.keys(byFac).forEach(k => {
    [["OPENING", "Opening"], ["NET MOVEMENTS", "Net movements"], ["EXPECTED", "Expected"]].forEach(([t, label]) => {
      if (!out.some(r => r.fac.toUpperCase().trim() === k && r.type.toUpperCase().trim() === t)) out.push({ fac: facName[k], type: label, values: rowsFor(k)[t] });
    });
  });
  return out;
}

// Tie-out per facility (and group) per date, all EUR:
// app accounts (Σ) vs facility Expected (must be 0 diff) and vs the workbook Net sheet,
// with the excluded T0 forecast as the explained difference.
function buildReconciliation(data) {
  const fx = c => data.fxRates[c] || 1;
  const n = data.dates.length;
  const masterOf = {};
  data.mapping.forEach(m => { masterOf[m.sub.toUpperCase().trim()] = (m.master || "").toUpperCase().trim(); });
  const t0 = {};
  (data.diagnostics?.t0Excluded || []).forEach(t => { t0[t.name] = t.value; });
  const sheetByName = {};
  (data.netSheetData || []).forEach(a => { sheetByName[a.name] = a.values; });
  const blank = () => ({ accountsEur: Array(n).fill(0), netSheetEur: Array(n).fill(0), t0ExcludedEur: Array(n).fill(0) });
  const groups = { "GROUP": blank() };
  data.facilityNames.forEach(f => { groups[f.toUpperCase().trim()] = blank(); });
  data.netData.forEach(a => {
    const cur = a.cur || "EUR";
    const targets = ["GROUP"];
    const k = masterOf[a.name.toUpperCase().trim()];
    if (k && groups[k]) targets.push(k);
    targets.forEach(g => {
      for (let i = 0; i < n; i++) {
        groups[g].accountsEur[i] += (a.values[i] || 0) / fx(cur);
        groups[g].netSheetEur[i] += (sheetByName[a.name]?.[i] || 0) / fx(cur);
        groups[g].t0ExcludedEur[i] += (t0[a.name] || 0) / fx(cur);
      }
    });
  });
  const expRow = f => data.facRows.find(r => r.fac.toUpperCase().trim() === f.toUpperCase().trim() && r.type.toUpperCase().trim() === "EXPECTED");
  const rows = ["GROUP", ...data.facilityNames].map(name => {
    const g = groups[name === "GROUP" ? "GROUP" : name.toUpperCase().trim()];
    const facExp = name === "GROUP" ? null : (expRow(name)?.values || Array(n).fill(0));
    return {
      name,
      byDate: data.dates.map((date, i) => ({
        date,
        accountsEur: g.accountsEur[i],
        facilityEur: facExp ? facExp[i] : null,
        diffAcctFac: facExp ? g.accountsEur[i] - facExp[i] : null,
        netSheetEur: g.netSheetEur[i],
        t0ExcludedEur: g.t0ExcludedEur[i],
        unexplained: g.netSheetEur[i] - g.accountsEur[i] - g.t0ExcludedEur[i]
      }))
    };
  });
  return { rows };
}

// Rolling forecast in horizon buckets (T0 / T+1 / T+2-5 / T+6-11) from the app's account balances.
// Daily flow per account = balance[i] - balance[i-1] (T0 flow = 0, T0 forecast is zeroed).
// stress: inflowHaircut % reduces positive flows, outflowShock % enlarges negative flows.
const FORECAST_BUCKETS = [["T0", 0, 0], ["T+1", 1, 1], ["T+2-5", 2, 5], ["T+6-11", 6, 11]];
function buildForecastBuckets(data, stress = {}, intercompanyLines = {}) {
  const n = data.dates.length;
  const hc = Math.max(0, Number(stress.inflowHaircut) || 0) / 100;
  const sh = Math.max(0, Number(stress.outflowShock) || 0) / 100;
  const toEur = (v, cur) => (cur === "EUR" || !data.fxRates[cur]) ? v : v / data.fxRates[cur];
  const masterOf = {};
  data.mapping.forEach(m => { masterOf[m.sub.toUpperCase().trim()] = (m.master || "").toUpperCase().trim(); });

  const accts = data.netData.map(a => {
    const cur = a.cur || "EUR";
    const v = a.values || [];
    const inflow = [], outflow = [], bal = [];
    for (let i = 0; i < n; i++) {
      const f = i === 0 ? 0 : (v[i] || 0) - (v[i - 1] || 0);
      inflow.push(f > 0 ? f * (1 - hc) : 0);
      outflow.push(f < 0 ? f * (1 + sh) : 0);
      bal.push(i === 0 ? (v[0] || 0) : bal[i - 1] + inflow[i] + outflow[i]);
    }
    return { name: a.name, cur, master: masterOf[a.name.toUpperCase().trim()] || "", inflow, outflow, bal };
  });

  const ccys = [...new Set(accts.map(a => a.cur))].sort();
  const ccyDaily = {};
  ccys.forEach(c => { ccyDaily[c] = { bal: Array(n).fill(0), inflow: Array(n).fill(0), outflow: Array(n).fill(0) }; });
  accts.forEach(a => { for (let i = 0; i < n; i++) { ccyDaily[a.cur].bal[i] += a.bal[i]; ccyDaily[a.cur].inflow[i] += a.inflow[i]; ccyDaily[a.cur].outflow[i] += a.outflow[i]; } });

  const facExpected = {};
  data.facilityNames.forEach(f => { facExpected[f] = Array(n).fill(0); });
  const facKey = {};
  data.facilityNames.forEach(f => { facKey[f.toUpperCase().trim()] = f; });
  accts.forEach(a => { const f = facKey[a.master]; if (f) for (let i = 0; i < n; i++) facExpected[f][i] += toEur(a.bal[i], a.cur); });
  const limitOf = (f, i) => {
    const r = data.facRows.find(x => x.fac.toUpperCase().trim() === f.toUpperCase().trim() && x.type.toUpperCase().trim() === "KREDIT FACILITET");
    return Math.abs((r?.values[i] ?? 0) + (intercompanyLines[f.toUpperCase().trim()] || 0));
  };
  const headroom = (f, i) => limitOf(f, i) + facExpected[f][i];

  const sum = (arr, s, e) => { let t = 0; for (let i = s; i <= e; i++) t += arr[i]; return t; };
  const buckets = FORECAST_BUCKETS.filter(([, s]) => s < n).map(([label, s, e0]) => {
    const e = Math.min(e0, n - 1);
    const byCcy = {};
    const group = { opening: 0, inflows: 0, outflows: 0, net: 0, closing: 0, min: Infinity };
    ccys.forEach(c => {
      const d = ccyDaily[c];
      const opening = s === 0 ? d.bal[0] : d.bal[s - 1];
      const inflows = sum(d.inflow, s, e), outflows = sum(d.outflow, s, e);
      let min = Infinity; for (let i = s; i <= e; i++) min = Math.min(min, d.bal[i]);
      byCcy[c] = { opening, inflows, outflows, net: inflows + outflows, closing: d.bal[e], min,
        openingEur: toEur(opening, c), inflowsEur: toEur(inflows, c), outflowsEur: toEur(outflows, c),
        netEur: toEur(inflows + outflows, c), closingEur: toEur(d.bal[e], c), minEur: toEur(min, c) };
      group.opening += byCcy[c].openingEur; group.inflows += byCcy[c].inflowsEur; group.outflows += byCcy[c].outflowsEur; group.closing += byCcy[c].closingEur;
    });
    group.net = group.inflows + group.outflows;
    for (let i = s; i <= e; i++) group.min = Math.min(group.min, ccys.reduce((t, c) => t + toEur(ccyDaily[c].bal[i], c), 0));
    const facilities = {};
    let totalHeadroom = 0, totalMinHeadroom = 0;
    data.facilityNames.forEach(f => {
      let minH = Infinity; for (let i = s; i <= e; i++) minH = Math.min(minH, headroom(f, i));
      facilities[f] = { expected: facExpected[f][e], limit: limitOf(f, e), headroom: headroom(f, e), minHeadroom: minH };
      totalHeadroom += facilities[f].headroom;
      totalMinHeadroom += minH;
    });
    return { label, startIdx: s, endIdx: e, startDate: data.dates[s], endDate: data.dates[e], byCcy, group, facilities, totalHeadroom, totalMinHeadroom };
  });

  const last = buckets[buckets.length - 1];
  const bridge = buckets.length ? {
    opening: buckets[0].group.opening,
    inflows: buckets.reduce((t, b) => t + b.group.inflows, 0),
    outflows: buckets.reduce((t, b) => t + b.group.outflows, 0),
    closing: last.group.closing, closingDate: last.endDate
  } : null;
  return { buckets, bridge, stressed: hc > 0 || sh > 0 };
}

// Forecast snapshot of the loaded file (app account balances for every date).
function makeForecastSnapshot(data) {
  const accounts = {};
  data.netData.forEach(a => { accounts[a.name] = { cur: a.cur || "EUR", values: [...(a.values || [])] }; });
  return { asOf: data.dates[0] || "", fileName: data.fileInfo?.name || "", savedAt: new Date().toISOString(), dates: [...data.dates], accounts };
}

// Keep one snapshot per as-of date (latest wins), newest 30.
function mergeSnapshots(prev, add) {
  const m = {};
  [...(prev || []), ...(add || [])].forEach(s => { if (s && s.asOf) m[s.asOf] = s; });
  return Object.values(m).sort((a, b) => new Date(a.asOf) - new Date(b.asOf)).slice(-30);
}

// Forecast vs actual: for every earlier snapshot whose dates include today's as-of date,
// compare its forecast balance for that date with today's actual Cash[T0], per account → currency → group (EUR at today's FX).
function buildForecastAccuracy(snapshots, data) {
  const asOf = data.dates[0];
  const toEur = (v, cur) => (cur === "EUR" || !data.fxRates[cur]) ? v : v / data.fxRates[cur];
  const actualOf = a => (data.cashData[a.name] ? data.cashData[a.name][0] : (a.values?.[0] || 0));
  const comparisons = (snapshots || []).filter(s => s && s.asOf !== asOf && Array.isArray(s.dates) && s.dates.indexOf(asOf) > 0).map(s => {
    const k = s.dates.indexOf(asOf);
    const byCcy = {};
    const group = { forecastEur: 0, actualEur: 0, varianceEur: 0, absVarianceEur: 0, absActualEur: 0 };
    const accounts = [];
    let missing = 0;
    data.netData.forEach(a => {
      const sa = s.accounts?.[a.name];
      if (!sa) { missing++; return; }
      const cur = a.cur || "EUR";
      const f = sa.values?.[k] || 0, act = actualOf(a), v = act - f;
      if (!byCcy[cur]) byCcy[cur] = { forecast: 0, actual: 0, variance: 0, absVariance: 0 };
      byCcy[cur].forecast += f; byCcy[cur].actual += act; byCcy[cur].variance += v; byCcy[cur].absVariance += Math.abs(v);
      group.forecastEur += toEur(f, cur); group.actualEur += toEur(act, cur); group.varianceEur += toEur(v, cur);
      group.absVarianceEur += Math.abs(toEur(v, cur)); group.absActualEur += Math.abs(toEur(act, cur));
      if (Math.abs(v) >= 0.005) accounts.push({ name: a.name, cur, forecast: f, actual: act, variance: v, varianceEur: toEur(v, cur) });
    });
    Object.entries(byCcy).forEach(([c, x]) => {
      Object.assign(x, { forecastEur: toEur(x.forecast, c), actualEur: toEur(x.actual, c), varianceEur: toEur(x.variance, c),
        absPctError: x.actual !== 0 ? Math.abs(x.variance) / Math.abs(x.actual) : null });
    });
    group.wape = group.absActualEur !== 0 ? group.absVarianceEur / group.absActualEur : null;
    group.bias = group.varianceEur >= 0 ? "actual ≥ forecast" : "actual < forecast";
    const notInSnapshot = missing, notInFile = Object.keys(s.accounts || {}).filter(n => !data.netData.some(a => a.name === n)).length;
    accounts.sort((x, y) => Math.abs(y.varianceEur) - Math.abs(x.varianceEur));
    return { snapshotAsOf: s.asOf, fileName: s.fileName, savedAt: s.savedAt, horizon: k, byCcy, group, topAccounts: accounts.slice(0, 10), accountsWithVariance: accounts.length, notInSnapshot, notInFile };
  });
  comparisons.sort((a, b) => a.horizon - b.horizon);
  return { asOf, comparisons };
}

function buildSuggestions(facRows, dates, facilityNames, odFacSet, mapping, fxRates, customTransfers, netData, minBalances = {}, poolMinOverrides = {}, globalODMin = 50000, ccyPriority = [], intercompanyLines = {}, globalTopUp = 0) {
  const START_DAY = 1;
  const SWEEP_MIN = 100000;
  const FX_MIN = 250000;
  const FX_LEAD = 3;
  const LOOKAHEAD = 5;
  const fx = c => fxRates[c] || 1;

  const built = buildLegs(netData, mapping, dates, fxRates);
  const legs = built.legs.map(l => ({ ...l, bal: [...l.balances] }));
  const accountToLeg = built.accountToLeg;

  (customTransfers || []).forEach(tf => {
    const fl = legs.find(l => l.key === accountToLeg[tf.from]);
    const tl = legs.find(l => l.key === accountToLeg[tf.to]);
    for (let i = 1; i < dates.length; i++) {
      if (fl) fl.bal[i] -= tf.amount;
      if (tl) tl.bal[i] += tf.amount;
    }
  });

  const legEur = (l, d) => l.bal[d] / fx(l.cur);
  const masters = [...new Set(legs.map(l => l.master))];
  const winEnd = d => Math.min(d + LOOKAHEAD, dates.length - 1);

  const masterLimit = (m, d) => {
    const r = facRows.find(x => x.fac.toUpperCase() === m && x.type.toUpperCase().includes("KREDIT"));
    return Math.abs(r?.values[d] ?? r?.values[0] ?? 0) + (intercompanyLines[m] || 0);
  };

  const masterEur = (m, d) => legs.filter(l => l.master === m).reduce((s, l) => s + legEur(l, d), 0);

  const floorEur = (l) => {
    const o = poolMinOverrides[(l.pool || "").toUpperCase().trim()];
    return (o !== undefined && o !== "" && !isNaN(Number(o))) ? Number(o) : globalODMin;
  };

  const spareEur = (l, d) => legEur(l, d) - floorEur(l);
  const worstLegEur = (l, d) => { let w = legEur(l, d); for (let i = d + 1; i <= winEnd(d); i++) w = Math.min(w, legEur(l, i)); return w; };
  const worstSpareEur = (l, d) => worstLegEur(l, d) - floorEur(l);

  const out = [];

  const move = (srcLeg, tgtLeg, txEur, d, reason) => {
    const isFX = srcLeg.cur !== tgtLeg.cur;
    let tradeDay = isFX ? d - FX_LEAD : d;
    if (tradeDay < START_DAY) { if (isFX) return false; tradeDay = START_DAY; }
    const srcLocal = Math.round(txEur * fx(srcLeg.cur));
    const tgtLocal = Math.round(txEur * fx(tgtLeg.cur));
    for (let i = d; i < dates.length; i++) {
      srcLeg.bal[i] -= srcLocal;
      tgtLeg.bal[i] += tgtLocal;
    }
    out.push({
      from: srcLeg.pool,
      to: tgtLeg.pool,
      fromMaster: srcLeg.master,
      toMaster: tgtLeg.master,
      amount: srcLocal,
      targetAmount: tgtLocal,
      date: dates[tradeDay],
      settleDate: dates[d],
      dayIdx: d,
      tradeDayIdx: tradeDay,
      reason,
      cur: srcLeg.cur,
      targetCur: tgtLeg.cur,
      untilIdx: null,
      settleBackDate: null
    });
    return true;
  };

  const capAvail = (s, d) => {
    let a = spareEur(s, d);
    const lim = masterLimit(s.master, d);
    if (lim > 0) a = Math.min(a, masterEur(s.master, d) + lim - floorEur(s));
    return a;
  };

  const capAvailWin = (s, d) => {
    let a = worstSpareEur(s, d);
    for (let i = d; i <= winEnd(d); i++) {
      const lim = masterLimit(s.master, i);
      if (lim > 0) a = Math.min(a, masterEur(s.master, i) + lim - floorEur(s));
    }
    return a;
  };

  for (let d = START_DAY; d < dates.length; d++) {
    let guard = 0;
    while (guard++ < 200) {
      let worst = null;
      masters.forEach(m => {
        const lim = masterLimit(m, d);
        if (lim <= 0) return;
        const hr = masterEur(m, d) + lim;
        if (hr < 0 && (!worst || hr < worst.hr)) worst = { m, hr };
      });
      if (!worst) break;

      let need = -worst.hr;
      const tgtLeg = legs.filter(l => l.master === worst.m).sort((a, b) => legEur(a, d) - legEur(b, d))[0];
      const srcs = legs.filter(l => l.master !== worst.m && spareEur(l, d) > 0).sort((a, b) => {
        const am = a.cur === tgtLeg.cur, bm = b.cur === tgtLeg.cur;
        if (am && !bm) return -1;
        if (bm && !am) return 1;
        return spareEur(b, d) - spareEur(a, d);
      });

      let funded = false;
      for (const s of srcs) {
        if (need <= 0) break;
        const avail = capAvail(s, d);
        if (avail <= 0) continue;
        let tx = Math.min(need, avail);
        if (tx < (s.cur === tgtLeg.cur ? SWEEP_MIN : FX_MIN)) continue;
        if (!move(s, tgtLeg, Math.round(tx), d, "Limit Breach")) continue;
        need -= tx;
        funded = true;
      }
      if (!funded) break;
    }

    legs.filter(l => worstLegEur(l, d) < 0).sort((a, b) => worstLegEur(a, d) - worstLegEur(b, d)).forEach(neg => {
      let need = globalTopUp - worstLegEur(neg, d);
      legs.filter(l => l !== neg && l.cur === neg.cur && worstSpareEur(l, d) > 0).sort((a, b) => worstSpareEur(b, d) - worstSpareEur(a, d)).forEach(s => {
        if (need <= 0) return;
        const avail = capAvailWin(s, d);
        if (avail <= 0) return;
        let tx = Math.min(need, avail);
        if (tx < SWEEP_MIN) return;
        if (!move(s, neg, Math.round(tx), d, "Intra-CCY Sweep")) return;
        need -= tx;
      });
    });

    legs.filter(l => worstLegEur(l, d) < 0).sort((a, b) => worstLegEur(a, d) - worstLegEur(b, d)).forEach(neg => {
      let need = globalTopUp - worstLegEur(neg, d);
      legs.filter(l => l.cur !== neg.cur && worstSpareEur(l, d) > 0).sort((a, b) => {
        const pa = ccyPriority.indexOf(a.cur), pb = ccyPriority.indexOf(b.cur);
        if (pa !== pb) return (pa < 0 ? 999 : pa) - (pb < 0 ? 999 : pb);
        return worstSpareEur(b, d) - worstSpareEur(a, d);
      }).forEach(s => {
        if (need <= 0) return;
        const avail = capAvailWin(s, d);
        if (avail <= 0) return;
        let tx = Math.min(need, avail);
        if (tx < FX_MIN) return;
        if (!move(s, neg, Math.round(tx), d, "FX Swap")) return;
        need -= tx;
      });
    });
  }

  return out;
}

// ── PARSE ─────────────────────────────────────────────────────────────────────
function parse(buffer) {
  const wb = XLSX.read(buffer, { type: "array" });
  const getSheet = (names) => {
    for (let n of names) {
      const s = wb.SheetNames.find(x => x.toLowerCase().replace(/\s/g, "") === n.toLowerCase().replace(/\s/g, ""));
      if (s) return XLSX.utils.sheet_to_json(wb.Sheets[s], { header: 1, defval: null });
    }
    return null;
  };
  


  let curSheet = "";
  const coerced = {};
  const noteCoerced = (v) => { coerced[curSheet] = coerced[curSheet] || { count: 0, samples: [] }; coerced[curSheet].count++; if (coerced[curSheet].samples.length < 5 && !coerced[curSheet].samples.includes(String(v))) coerced[curSheet].samples.push(String(v)); };

  const num = (v) => {
    if (v == null || v === "") return 0;
    if (String(v).toLowerCase().includes("error")) { noteCoerced(v); return 0; }
    if (typeof v === "number") return v;
    let s = String(v).toUpperCase().trim();
    let isNeg = s.includes("(") || s.includes("-");
    s = s.replace(/[^0-9.,]/g, "");
    if (!s) { noteCoerced(v); return 0; }
    if (s.includes(",") && s.includes(".")) {
      if (s.indexOf(".") < s.indexOf(",")) s = s.replace(/\./g, "").replace(",", ".");
      else s = s.replace(/,/g, "");
    } else if (s.includes(",")) s = s.replace(",", ".");
    let n = parseFloat(s);
    return isNeg ? -Math.abs(n) : Math.abs(n);
  };

const dateSet = new Set(); ["Net"].forEach(sn => { const raw = getSheet([sn]); if (raw && raw.length > 0) { raw[0].forEach((h, idx) => { if (idx >= 5 && h) { const dStr = formatExcelDate(h); if (dStr) dateSet.add(dStr); } }); } });

  const dates = Array.from(dateSet).sort((a, b) => new Date(a) - new Date(b));

  const facMap = new Map();
  let toDates = [];
  for (const sn of ["Total overview", "Total_overview"]) {
    const raw = getSheet([sn]);
    if (!raw || raw.length < 2) continue;
    curSheet = "Total overview";

    const hdr = raw[0];
    toDates = hdr.filter((h, idx) => idx >= 2 && h).map(h => formatExcelDate(h));
    for (let i = 1; i < raw.length; i++) {
      const fac = String(raw[i][0] || "").trim();
      const type = String(raw[i][1] || "").trim();
      if (!fac || !type) continue;
      
      const key = `${fac.toUpperCase()}|${type.toUpperCase()}`;
      if (!facMap.has(key)) facMap.set(key, { fac, type, rawValues: {} });
      
      const entry = facMap.get(key);
      hdr.forEach((h, idx) => {
        if (idx >= 2 && h) {
          const dStr = formatExcelDate(h);
          entry.rawValues[dStr] = num(raw[i][idx]);
        }
      });
    }
    break;
  }

const facRows = Array.from(facMap.values()).map(r => { 
const values = dates.map(d => r.rawValues[d] ?? 0); if (r.type.toUpperCase().includes("KREDIT")) { 
const firstKnown = values.find(v => v !== 0) || 0; let last = firstKnown; for (let i = 0; i < values.length; i++) { if (values[i] !== 0) last = values[i]; else values[i] = last; } } if (r.type.toUpperCase().includes("EXPECTED")) { let last = null; for (let i = 0; i < dates.length; i++) { if (r.rawValues[dates[i]] !== undefined) last = r.rawValues[dates[i]]; else if (last !== null) values[i] = last; } } return { fac: r.fac, type: r.type, values }; });
  const facilityNames = [...new Set(facRows.map(r => r.fac))];

  const strRaw = getSheet(["OD_Struktur"]) || [];
  const norm = s => String(s || "").toLowerCase().replace(/[\s_]/g, "");
  let hi = strRaw.findIndex(r => r.some(c => norm(c).includes("subaccount")));
  const sh = (strRaw[hi] || []).map(c => norm(c));

  const cMaster = sh.findIndex(h => h.includes("master"));
  const cPool = sh.findIndex(h => h.includes("pool") && !h.includes("master"));
  const cSub = sh.findIndex(h => h.includes("subaccount"));
  const cVal = sh.findIndex(h => h.includes("valuta") || h.includes("currency"));
  const cOD = sh.findIndex(h => h.includes("odallowed"));

  const mapping = [];
  for (let i = hi + 1; i < strRaw.length; i++) {
    const r = strRaw[i];
    if (r[cSub]) mapping.push({ 
      master: String(r[cMaster] || ""), pool: String(r[cPool] || ""), 
      sub: String(r[cSub]), ccy: String(r[cVal] || ""), 
      odAllowed: String(r[cOD]).toLowerCase().includes("y") 
    });
  }

  let fxRatesRaw = getSheet(["FX", "FX_Rates"]) || [];
  const fxRates = { "EUR": 1 };
  if (fxRatesRaw.length > 1) {
    curSheet = "FX";
    const hdr = fxRatesRaw[0].map(c => String(c).toLowerCase());
    const cC = hdr.findIndex(h => h.includes("cur"));
    const cR = hdr.findIndex(h => h.includes("mid") || h.includes("rate"));
    fxRatesRaw.slice(1).forEach(r => { if(r[cC]) fxRates[String(r[cC]).toUpperCase()] = num(r[cR]); });
  }

  const netRaw = getSheet(["Net"]) || [];
  const netData = [];
  if (netRaw.length > 1) {
    curSheet = "Net";
    const netHdr = netRaw[0];
    for (let i = 1; i < netRaw.length; i++) {
      const name = String(netRaw[i][3] || "").trim();
      if (!name) continue;
      const values = dates.map(d => {
        const colIdx = netHdr.findIndex(h => formatExcelDate(h) === d);
        return colIdx >= 0 ? num(netRaw[i][colIdx]) : 0;
      });
      netData.push({ name, cur: String(netRaw[i][2] || "").toUpperCase(), values, entity: String(netRaw[i][0] ?? "").trim(), bank: String(netRaw[i][1] ?? "").trim() });
    }
  }

  const sheetDates = { "Net": dates.length, "Total overview": toDates.length };
  const t0Excluded = [];
  const parseAccountSheet = (sheetName, opts = {}) => {
    const raw = getSheet([sheetName]) || [];
    const out = {};
    if (raw.length > 1) {
      curSheet = sheetName;
      const hdr = raw[0];
      sheetDates[sheetName] = hdr.filter((h, idx) => idx >= 5 && h).length;
      for (let i = 1; i < raw.length; i++) {
        const name = String(raw[i][3] || "").trim();
        if (!name) continue;
        let vals = dates.map(d => {
          const ci = hdr.findIndex(h => formatExcelDate(h) === d);
          return ci >= 0 ? num(raw[i][ci]) : 0;
        });
        if (opts.zeroT0) { if (vals[0]) t0Excluded.push({ name, value: vals[0] }); vals[0] = 0; }
        if (opts.cumulative) { let run = 0; vals = vals.map(v => (run += v)); }
        out[name] = vals;
      }
    }
    return out;
  };
  const forecastData = parseAccountSheet("Forecast", { zeroT0: true });
  const cashData = parseAccountSheet("Cash");
  const netSheetData = netData.map(a => ({ name: a.name, cur: a.cur, values: [...a.values] }));

  netData.forEach(a => {
    const c = cashData[a.name];
    const f = forecastData[a.name];
    if (!c && !f) return;
    const base = c ? c[0] : 0;
    let run = 0;
    a.values = dates.map((_, i) => { run += f ? f[i] : 0; return base + run; });
  });

  const derivedFacRows = deriveFacilityRows(netData, mapping, fxRates, dates, facRows);

  const toExpectedRaw = {};
  facMap.forEach(r => { if (r.type.toUpperCase().includes("EXPECTED")) toExpectedRaw[r.fac.toUpperCase().trim()] = r.rawValues; });
  const mappedSubs = new Set(mapping.map(m => m.sub.toUpperCase().trim()));
  const netNames = new Set(netData.map(a => a.name.toUpperCase().trim()));
  const diagnostics = {
    asOf: dates[0] || "", horizonEnd: dates[dates.length - 1] || "",
    sheetDates, toDates, toExpectedRaw, coerced,
    missingFx: [...new Set(netData.map(a => a.cur).filter(c => c && !fxRates[c]))],
    unmapped: netData.filter(a => !mappedSubs.has(a.name.toUpperCase().trim())).map(a => a.name),
    strukturNotInNet: mapping.filter(m => !netNames.has(m.sub.toUpperCase().trim())).map(m => m.sub),
    t0Excluded: t0Excluded.map(t => ({ ...t, cur: (netData.find(a => a.name === t.name) || {}).cur || "" }))
  };

  return { dates, facilityNames, facRows: derivedFacRows, mapping, netData, fxRates, forecastData, cashData, netSheetData, diagnostics };
}

// ── HELPER UI COMPONENTS ─────────────────────────────────────────────────────
const TH = ({ l, rs, cs, center, hi, r }) => (
  <th rowSpan={rs||1} colSpan={cs||1} style={{
    padding: "8px 8px",
    textAlign: center ? "center" : (r ? "right" : "left"),
    color: hi ? C.text : C.muted,
    fontSize: 10,
    fontWeight: hi ? 700 : 600,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: hi ? `1px solid ${C.border}20` : "none"
  }}>{l}</th>
);

const SC = ({ s, checked, onToggle }) => {
  const isBreach = s.reason && s.reason.includes("Breach");
  const isFX = s.cur !== s.targetCur;
  const rate = isFX && s.amount ? (s.targetAmount / s.amount) : null;
  return (
    <div style={{ padding: 10, background: checked ? `${C.accent}10` : "#ffffff03", border: `1px solid ${checked ? C.accent : C.border}`, borderRadius: 6, marginBottom: 8, display: "flex", gap: 8, alignItems: "center" }}>
      <input type="checkbox" checked={!!checked} onChange={onToggle} style={{ cursor: "pointer", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 9, color: isFX ? C.warn : (isBreach ? C.neg : C.accent), fontWeight: 700, marginBottom: 2 }}>
          {isFX ? "💱 FX SWAP" : "🔁 SWEEP"} · {(s.reason || "").toUpperCase()}
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: C.text }}>
          {s.from} <span style={{ color: C.muted, margin: "0 4px" }}>→</span> {s.to}
        </div>
        <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>Trade: {s.date} · Settle: {s.settleDate}</div>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: C.text }}>{fmt(s.amount)} <span style={{ fontSize: 9, color: C.muted }}>{s.cur}</span></div>
        {isFX && <div style={{ fontSize: 10, fontWeight: 700, color: C.pos }}>→ {fmt(s.targetAmount)} {s.targetCur}</div>}
        {isFX && rate && <div style={{ fontSize: 8, color: C.muted }}>@ {rate.toFixed(4)}</div>}
      </div>
    </div>
  );
};

const SuggFeed = ({ items, dates, selected, toggle, suggKey, emptyMsg }) =>
  items.length === 0
    ? <div style={{ padding: 20, textAlign: "center", color: C.muted, fontSize: 11 }}>{emptyMsg}</div>
    : dates.map((_, dIdx) => dIdx).map(dIdx => {
        const dayItems = items.filter(s => (s.tradeDayIdx !== undefined ? s.tradeDayIdx : s.dayIdx) === dIdx);
        if (!dayItems.length) return null;
        return (
          <div key={dIdx} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
              <span>📅 {dates[dIdx] || `Day ${dIdx}`}</span>
              <div style={{ flex: 1, height: 1, background: `${C.border}50` }} />
            </div>
            {dayItems.map((s, i) => { const k = suggKey(s); return <SC key={k+i} s={s} checked={selected.has(k)} onToggle={() => toggle(k)} />; })}
          </div>
        );
      });

const PoolTreeNode = ({ name, ccy, subs }) => (
  <div style={{ padding: 8, background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, minWidth: 120 }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: C.text, textAlign: "center", marginBottom: 6 }}>
      {name} <span style={{ color: C.muted, fontSize: 8 }}>{ccy}</span>
    </div>
    {subs && subs.map(sub => (
      <div key={sub} style={{ fontSize: 9, color: C.muted, textAlign: "center", padding: "2px 0", borderTop: `1px solid ${C.border}30` }}>{sub}</div>
    ))}
  </div>
);

const VisualFacilityMonitor = ({ data, fv, collapsed, onToggle }) => {
  if (!data || !data.facilityNames) return null;
  const daysToShow = Math.min(12, data.dates.length);

  

const cellColor = (headroom, limit) => {
    if (limit <= 0) {
      if (headroom < 0) return { bg: `${C.neg}40`, fg: C.neg };
      return { bg: `hsl(130, 50%, 26%)`, fg: "#f8fafc" };
    }
    const ratio = headroom / limit;
    if (ratio < 0) return { bg: `${C.neg}40`, fg: C.neg };
    const clamped = Math.max(0, Math.min(1, ratio));
    const hue = clamped * 130;                 // 0=red → 130=green
    const sat = 70 - clamped * 20;             // softer when green
    const light = 22 + clamped * 8;            // subtle brighten
    return { bg: `hsl(${hue}, ${sat}%, ${light}%)`, fg: "#f8fafc" };
  };

  return (
<div style={{ borderTop: `1px solid ${C.border}`, display: "flex", flexDirection: "column", flexShrink: 0 }}>


{!collapsed && (<div style={{ overflowX: "auto", maxHeight: 260, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
          <thead style={{ position: "sticky", top: 0, background: C.card, zIndex: 2 }}>
            <tr>
              <th style={{ padding: "6px 10px", textAlign: "left", color: C.muted, fontSize: 9, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>Facility</th>
              {Array.from({ length: daysToShow }).map((_, i) => (
                <th key={i} style={{ padding: "6px 4px", textAlign: "center", color: C.muted, fontSize: 9, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>
                  {(data.dates[i] || "").slice(0, 6)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.facilityNames.map(fac => {
              const limit = Math.abs(fv(fac, "Kredit facilitet", 0));
              return (
                <tr key={fac} style={{ borderBottom: `1px solid ${C.border}20` }}>
                  <td style={{ padding: "6px 10px", fontSize: 10, fontWeight: 700, color: C.text, whiteSpace: "nowrap" }} title={fac}>{fac}</td>
                  {Array.from({ length: daysToShow }).map((_, i) => {
                    const headroom = fv(fac, "HEADROOM", i);
                    const opening = fv(fac, "Opening", i);
                    const limD = Math.abs(fv(fac, "Kredit facilitet", i));
                    const cashHr = opening + limD;
                    const fcast = headroom - cashHr;
                    const fcBreach = headroom < 0 && cashHr >= 0;
                    const { bg, fg } = cellColor(headroom, limit);
                    return (
                      <td key={i} style={{ padding: "4px 6px", textAlign: "right", background: bg, color: fg, fontWeight: 700, borderLeft: `1px solid ${C.bg}`, boxShadow: fcBreach ? `inset 0 0 0 2px ${C.warn}` : "none" }}
                        title={`${fac} · ${data.dates[i]}\nHeadroom ${fmt(headroom)} = cash ${fmt(cashHr)} + forecast ${fmt(fcast)}\nLimit ${fmt(limD)}${fcBreach ? "\n⚠ forecast-driven (confirmed cash still ≥ 0)" : ""}`}>
                        {(headroom / 1000000).toFixed(1)}
                        <div style={{ fontSize: 7, fontWeight: 500, opacity: 0.75, color: fcast < 0 ? C.warn : "inherit" }}>{fcast >= 0 ? "+" : ""}{(fcast / 1000000).toFixed(1)}f</div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
</table>
      </div>)}
    </div>
  );
};

// ── APP ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [data,setData]=useState(null);
  const [ruleSearch, setRuleSearch] = useState("");
  const [expandedFacs, setExpandedFacs] = useState(new Set());
  const [expCur, setExpCur] = useState(new Set());
  const [expPoolCur, setExpPoolCur] = useState(new Set());
  const [tab,setTab]=useState("positions");
  const [posView, setPosView] = useState("top"); 
  const [drag,setDrag]=useState(false);
  const [err,setErr]=useState(null);
const transferOptions = useMemo(() => {
    return data?.netData ? data.netData.map(a => a.name).sort() : [];
  }, [data]);  const [showForecast, setShowForecast] = useState(true);
  const [applySuggestions, setApplySuggestions] = useState(false);
  const [expAccounts, setExpAccounts] = useState(new Set());
  const [expBank, setExpBank] = useState(new Set());
  const [customTransfers, setCustomTransfers] = useState([]);
  const [tfFrom, setTfFrom] = useState("");
  const [tfTo, setTfTo] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [expPoolDetails, setExpPoolDetails] = useState(new Set());
  const [tfAmt, setTfAmt] = useState("");
  const [showOdMatrix, setShowOdMatrix] = useState(true); 
  const [showSweeps, setShowSweeps] = useState(true);
  const [showFx, setShowFx] = useState(true);  
  const [odAccess, setOdAccess] = useState(() => { try { const saved = localStorage.getItem("treasury_od_access"); return saved ? JSON.parse(saved) : {}; } catch (e) { return {}; } });
  useEffect(() => { try { localStorage.setItem("treasury_od_access", JSON.stringify(odAccess)); } catch (e) {} }, [odAccess]);
  
  const [globalODMin, setGlobalODMin] = useState(() => {
  try { const s = localStorage.getItem("treasury_global_od_min"); return s ? JSON.parse(s) : 200000; } catch { return 200000; }
});
useEffect(() => { try { localStorage.setItem("treasury_global_od_min", JSON.stringify(globalODMin)); } catch {} }, [globalODMin]);

const [globalTopUp, setGlobalTopUp] = useState(() => {
  try { const s = localStorage.getItem("treasury_global_topup"); return s ? JSON.parse(s) : 0; } catch { return 0; }
});
useEffect(() => { try { localStorage.setItem("treasury_global_topup", JSON.stringify(globalTopUp)); } catch {} }, [globalTopUp]);

const [intercompanyLines, setIntercompanyLines] = useState(() => {
  try { const s = localStorage.getItem("treasury_intercompany_lines"); return s ? JSON.parse(s) : {}; } catch { return {}; }
});
useEffect(() => { try { localStorage.setItem("treasury_intercompany_lines", JSON.stringify(intercompanyLines)); } catch {} }, [intercompanyLines]);
const [selectedSugg, setSelectedSugg] = useState(new Set());
const suggKey = (s) => `${s.reason}_${s.from}_${s.to}_${s.cur}_${s.dayIdx}`;
const toggleSugg = (k) => setSelectedSugg(p => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
const sweeps = useMemo(() => suggestions.filter(s => s.cur === s.targetCur), [suggestions]);
const fxTrades = useMemo(() => suggestions.filter(s => s.cur !== s.targetCur), [suggestions]);

const [minBalances, setMinBalances] = useState(() => {
    try { const saved = localStorage.getItem("treasury_min_balances"); return saved ? JSON.parse(saved) : {}; } catch (e) { return {}; }
  });
  useEffect(() => { try { localStorage.setItem("treasury_min_balances", JSON.stringify(minBalances)); } catch (e) {} }, [minBalances]);

const [scale, setScale] = useState(1);
useEffect(() => {
  const update = () => {
    const designWidth = 1600;
    setScale(Math.min(1.2, Math.max(0.6, window.innerWidth / designWidth)));
  };
  update();
  window.addEventListener("resize", update);
  return () => window.removeEventListener("resize", update);
}, []);

const [copied, setCopied] = useState("");

const copyText = (text, label) => {
  navigator.clipboard.writeText(text);
  setCopied(label);
  setTimeout(() => setCopied(""), 1500);
};

const buildTransferEmail = (rows) => {
  if (!rows || !rows.length) return "";
  return rows.map(s =>
    `${s.date}: ${s.from} → ${s.to} ${fmt(s.amount)} ${s.cur}`
  ).join("\n");
};

const buildFxHedging = (rows) => {
  if (!rows || !rows.length) return "";
  return rows
    .filter(s => s.cur !== s.targetCur)
    .map(s =>
      `SELL ${fmt(s.amount)} ${s.cur} → BUY ${fmt(s.targetAmount)} ${s.targetCur}`
    )
    .join("\n");
};

const [ccyPriority, setCcyPriority] = useState(() => {
  try { const s = localStorage.getItem("treasury_ccy_priority"); return s ? JSON.parse(s) : []; } catch { return []; }
});
useEffect(() => { try { localStorage.setItem("treasury_ccy_priority", JSON.stringify(ccyPriority)); } catch {} }, [ccyPriority]);

useEffect(() => {
  if (!data) return;
  const allCcys = [...new Set(data.netData.map(a => a.cur).filter(Boolean))];
  setCcyPriority(prev => {
    const known = new Set(prev);
    const newCcys = allCcys.filter(c => !known.has(c));
    if (!newCcys.length) return prev;
    const defaults = ["EUR","USD","GBP","DKK","SEK","NOK"];
    const merged = [...prev];
    defaults.forEach(d => { if (newCcys.includes(d) && !merged.includes(d)) merged.push(d); });
    newCcys.filter(c => !defaults.includes(c)).sort().forEach(c => merged.push(c));
    return merged;
  });
}, [data]);

  const [poolMinOverrides, setPoolMinOverrides] = useState(() => { 
    try { const saved = localStorage.getItem("treasury_pool_mins"); return saved ? JSON.parse(saved) : {}; } catch (e) { return {}; } 
  });
  useEffect(() => { try { localStorage.setItem("treasury_pool_mins", JSON.stringify(poolMinOverrides)); } catch (e) {} }, [poolMinOverrides]);
  
const load = useCallback(file => {
    if (!file) return;
    const r = new FileReader();
    r.onload = e => { try { const d = parse(new Uint8Array(e.target.result)); d.fileInfo = { name: file.name, lastModified: file.lastModified, loadedAt: Date.now() }; setData(d); setErr(null); } catch (ex) { setErr(ex.message); } };
    r.readAsArrayBuffer(file);
  }, []);

  // Audit trail of rule/setting changes (debounced, persisted)
  const [auditLog, setAuditLog] = useState(() => { try { const s = localStorage.getItem("treasury_audit_log"); return s ? JSON.parse(s) : []; } catch { return []; } });
  useEffect(() => { try { localStorage.setItem("treasury_audit_log", JSON.stringify(auditLog)); } catch {} }, [auditLog]);
  const [stress, setStress] = useState({ inflowHaircut: 0, outflowShock: 0 });
  const auditSettings = { globalODMin, globalTopUp, poolMinOverrides, minBalances, odAccess, ccyPriority, intercompanyLines, stress };
  const auditKey = JSON.stringify(auditSettings);
  const auditPrev = React.useRef(null);
  useEffect(() => {
    const t = setTimeout(() => {
      const cur = JSON.parse(auditKey);
      const prev = auditPrev.current;
      auditPrev.current = cur;
      if (!prev) return;
      const entries = Object.keys(cur).filter(k => JSON.stringify(cur[k]) !== JSON.stringify(prev[k]))
        .map(k => ({ ts: new Date().toISOString(), setting: k, from: JSON.stringify(prev[k]), to: JSON.stringify(cur[k]) }));
      if (entries.length) setAuditLog(l => [...l, ...entries].slice(-500));
    }, 1000);
    return () => clearTimeout(t);
  }, [auditKey]);
  useEffect(() => {
    if (!data) return;
    setAuditLog(l => [...l, { ts: new Date().toISOString(), setting: "file loaded", from: "", to: `${data.fileInfo?.name || ""} · as of ${data.dates[0] || ""}` }].slice(-500));
  }, [data]);

  // Forecast snapshots (last 30, one per as-of date) for forecast-vs-actual
  const [fcSnapshots, setFcSnapshots] = useState(() => { try { const s = localStorage.getItem("treasury_fc_snapshots"); return s ? JSON.parse(s) : []; } catch { return []; } });
  useEffect(() => { try { localStorage.setItem("treasury_fc_snapshots", JSON.stringify(fcSnapshots)); } catch {} }, [fcSnapshots]);
  useEffect(() => { if (data) setFcSnapshots(p => mergeSnapshots(p, [makeForecastSnapshot(data)])); }, [data]);
  const accuracy = useMemo(() => data ? buildForecastAccuracy(fcSnapshots, data) : { comparisons: [] }, [data, fcSnapshots]);

  const [dqDate, setDqDate] = useState(0);
  const reconciliation = useMemo(() => data ? buildReconciliation(data) : { rows: [] }, [data]);
  const forecastBuckets = useMemo(() => data ? buildForecastBuckets(data, stress, intercompanyLines) : { buckets: [], bridge: null }, [data, stress, intercompanyLines]);

  const odFacSet = useMemo(() => {
    if (!data) return new Set();
    const customAllowed = Object.keys(odAccess).filter(k => odAccess[k]);
    const tops = data.mapping.filter(m => m.odAllowed || customAllowed.includes(m.sub)).map(m => m.master.toLowerCase());
    return new Set(data.facilityNames.filter(fac => {
      const odLim = Math.abs(data.facRows.find(r => r.fac.toUpperCase().trim() === fac.toUpperCase().trim() && r.type.toUpperCase().trim() === "KREDIT FACILITET")?.values[0] ?? 0);
      return tops.some(k => fac.toLowerCase().includes(k.split("_")[0])) || odLim > 0;
    }));
  }, [data, odAccess]);

  const detailByMaster = useMemo(() => {
    if (!data) return {};
    return data.mapping.reduce((acc, m) => {
      const masterKey = (m.master || "").toUpperCase().trim();
      const poolKey = (m.pool || "").toUpperCase().trim();
      if (masterKey) (acc[masterKey] = acc[masterKey] || []).push(m);
      if (poolKey && poolKey !== masterKey) {
        if (!acc[poolKey]) acc[poolKey] = [];
        if (!acc[poolKey].some(x => x.sub === m.sub)) acc[poolKey].push(m);
      }
      return acc;
    }, {});
  }, [data]);

const detailHierarchy = useMemo(() => {
  if (!data || !data.netData || !data.dates) return {};
  const tree = {};
	(data.mapping || []).forEach(m => {
  const cur = (m.ccy || "EUR").toUpperCase();
  const poolName = (m.pool || m.master || "").trim();
  if (!poolName) return;
  if (!tree[cur]) tree[cur] = { cur, pools: {}, standalone: [], totals: data.dates.map(() => 0) };
  if (!tree[cur].pools[poolName]) {
    tree[cur].pools[poolName] = { name: poolName, accounts: [], totals: data.dates.map(() => 0) };
  }
});
    
data.netData.forEach(acct => {
	const cur = acct.cur || "EUR";
      const mapping = data.mapping?.find(x => x.sub.toUpperCase().trim() === acct.name.toUpperCase().trim());
      
      // NEW LOGIC: Visually group by Pool first, fall back to Master
      const poolVal = (mapping?.pool || "").trim();
      const masterVal = (mapping?.master || "").trim();
      const poolName = poolVal || masterVal || null;
      
      if (!tree[cur]) tree[cur] = { cur, pools: {}, standalone: [], totals: data.dates.map(() => 0) };
      
      const safeValues = acct.values || data.dates.map(() => 0);
      const activeValues = showForecast ? safeValues : data.dates.map(() => safeValues[0] || 0);
      const acctData = { ...acct, values: activeValues };
      
      // THIS IS THE BLOCK THAT WAS MISSING/BROKEN
      if (poolName) {
if (!tree[cur].pools[poolName]) tree[cur].pools[poolName] = { name: poolName, accounts: [], totals: data.dates.map(() => 0) };
tree[cur].pools[poolName].accounts.push(acctData);
activeValues.forEach((v, i) => { if (tree[cur].pools[poolName].totals[i] !== undefined) tree[cur].pools[poolName].totals[i] += v; });
      } else {
        tree[cur].standalone.push(acctData);
      }
      
      activeValues.forEach((v, i) => { if (tree[cur].totals[i] !== undefined) tree[cur].totals[i] += v; });
    });
    
    return tree;
  }, [data, showForecast]);

const suggestionAdj = useMemo(() => {
    if (!data) return {};
    const adj = {};
    suggestions.filter(s => selectedSugg.has(suggKey(s))).forEach(s => {
      if (!adj[s.from]) adj[s.from] = data.dates.map(() => 0);
      if (!adj[s.to])   adj[s.to]   = data.dates.map(() => 0);
for (let i = s.dayIdx; i < (s.untilIdx ?? data.dates.length); i++) {
	adj[s.from][i] -= s.amount;
        adj[s.to][i]   += (s.cur === s.targetCur ? s.amount : s.targetAmount);
      }
    });
    return adj;
  }, [data, suggestions, selectedSugg]);

const facilityAdj = useMemo(() => {
  if (!data || !suggestions.length) return {};
  const adj = {};
  suggestions.filter(s => selectedSugg.has(suggKey(s))).forEach(s => {
    const fromMaster = (s.fromMaster || "").toUpperCase().trim();
    const toMaster = (s.toMaster || "").toUpperCase().trim();
    if (fromMaster) {
      if (!adj[fromMaster]) adj[fromMaster] = data.dates.map(() => 0);
      const fromEur = s.amount / (data.fxRates[s.cur] || 1);
      for (let i = s.dayIdx; i < (s.untilIdx ?? data.dates.length); i++) { adj[fromMaster][i] -= fromEur; }
    }
    if (toMaster) {
      if (!adj[toMaster]) adj[toMaster] = data.dates.map(() => 0);
      const toEur = s.targetAmount / (data.fxRates[s.targetCur] || 1);
      for (let i = s.dayIdx; i < (s.untilIdx ?? data.dates.length); i++) { adj[toMaster][i] += toEur; }
    }
  });
  return adj;
}, [data, suggestions, selectedSugg]);
    
  const cashPoolStructure = useMemo(() => {
    if (!data) return {};
    const banks = {};
    data.mapping.forEach(m => {
      const bank = m.master ? m.master.split("_")[0] : "Other";
      if (!banks[bank]) banks[bank] = {};
      
      const masterKey = m.master || m.sub;
      if (!banks[bank][masterKey]) banks[bank][masterKey] = { name: masterKey, pools: {} };
      
      const poolKey = m.pool || "No Pool";
      if (!banks[bank][masterKey].pools[poolKey]) banks[bank][masterKey].pools[poolKey] = { name: poolKey, ccy: m.ccy, subs: [] };
      
      if (!banks[bank][masterKey].pools[poolKey].subs.includes(m.sub)) {
        banks[bank][masterKey].pools[poolKey].subs.push(m.sub);
      }
    });
    return banks;
  }, [data]);

  const fv = (fac, type, d) => {
    if (!data) return 0;
    const typeU = type.toUpperCase().trim();
    if (typeU === "HEADROOM") {
      const exp = fv(fac, "Expected", d);
      const lim = Math.abs(fv(fac, "Kredit facilitet", d));
      return lim + exp;
    }
	
    if (typeU === "UTILISATION %") {
      const exp = fv(fac, "Expected", d);
      const lim = Math.abs(fv(fac, "Kredit facilitet", d));
      return lim > 0 ? Math.abs(Math.min(0, exp)) / lim : 0;
    }
    const row = data.facRows.find(r => r.fac.toUpperCase().trim() === fac.toUpperCase().trim() && r.type.toUpperCase().trim() === typeU);
let baseVal = row?.values[d] ?? 0;
    if (typeU === "KREDIT FACILITET") baseVal += (intercompanyLines[fac.toUpperCase().trim()] || 0);
if (applySuggestions && typeU === "EXPECTED") {
      const k = fac.toUpperCase().trim();
      if (facilityAdj[k]?.[d] !== undefined) baseVal += facilityAdj[k][d];
    }
    return baseVal;
  };
  
const activeBreaches = useMemo(() => {
  if (!data) return [];

  const map = new Map();

  data.facilityNames.forEach(fac => {

    const expRow = data.facRows.find(r =>
      r.fac.toUpperCase().trim() === fac.toUpperCase().trim() &&
      r.type.toUpperCase().includes("EXPECTED")
    );

    const limRow = data.facRows.find(r =>
      r.fac.toUpperCase().trim() === fac.toUpperCase().trim() &&
      r.type.toUpperCase().includes("KREDIT")
    );

    if (!expRow || !limRow) return;

    const k = fac.toUpperCase().trim();
    let worst = null;

    for (let d = 0; d < data.dates.length; d++) {

      const exp = (expRow.values[d] ?? 0) + (applySuggestions ? (facilityAdj[k]?.[d] || 0) : 0);
      const lim = Math.abs(limRow.values[d] ?? 0);
      const headroom = lim + exp;

      if (headroom < 0) {
        if (!worst || headroom < worst.headroom) {
          worst = {
            fac,
            date: data.dates[d],
            headroom
          };
        }
      }
    }

    if (worst) {
      map.set(fac, worst);
    }

  });

  return Array.from(map.values());
}, [data, applySuggestions, facilityAdj]);
  
  const getEurVal = (val, cur) => (!data || cur === "EUR" || !data.fxRates[cur]) ? val : val / data.fxRates[cur];

const exportCsv = (rows, name) => {
    const header = ["Type","Reason","From","To","Amount","Cur","TargetAmount","TargetCur","TradeDate","SettleDate"];
    const esc = v => `"${String(v ?? "").replace(/"/g,'""')}"`;
    const lines = [header.join(",")].concat(rows.map(s => [
      s.cur===s.targetCur?"SWEEP":"FX", s.reason, s.from, s.to, s.amount, s.cur, s.targetAmount, s.targetCur, s.date, s.settleDate
    ].map(esc).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
  };
  
   const buildCashSummary = () => {
    if (!data) return "";
    const col = 0;
    let out = `GROUP CASH POSITION  -  ${data.dates[0] || ""}\n=====================================\n\nBY CURRENCY (local | EUR):\n`;
    let grandEur = 0;
    Object.entries(displayHierarchy).sort((a, b) => a[0].localeCompare(b[0])).forEach(([cur, cd]) => {
      const local = cd.totals[col]; const eur = getEurVal(local, cur); grandEur += eur;
      out += `  ${cur.padEnd(4)} ${String(fmt(local)).padStart(16)} ${cur}   ( ${fmt(eur)} EUR )\n`;
    });
    out += `\n  GROUP TOTAL  ${fmt(grandEur)} EUR\n\nBY MASTER FACILITY (EUR):\n`;
    data.facilityNames.forEach(fac => {
      const exp = fv(fac, "Expected", col);
      const lim = Math.abs(fv(fac, "Kredit facilitet", col));
      out += `  ${fac.padEnd(22)} bal ${String(fmt(exp)).padStart(14)}   limit ${String(fmt(lim)).padStart(12)}   headroom ${String(fmt(lim + exp)).padStart(14)}\n`;
    });
	return out;
  };

const displayHierarchy = useMemo(() => {
  if (!applySuggestions) return detailHierarchy;

    const adjusted = {};
    
Object.entries(detailHierarchy).forEach(([cur, curData]) => {
      const newPools = {};
      const newTotals = data.dates.map(() => 0);
      Object.entries(curData.pools).forEach(([poolKey, pool]) => {
        const adjArray = suggestionAdj[pool.name];
        const adjustedPoolTotals = pool.totals.map((v, i) => v + (adjArray?.[i] || 0));
        let adjustedAccounts = pool.accounts.map(a => ({ ...a, values: [...a.values] }));
        if (adjArray) {
          // HORIZON EXTENSION: Replaced data.dates.length limit with Math.min(11, data.dates.length)
          const horizonLimit = Math.min(11, data.dates.length);
          for (let i = 0; i < horizonLimit; i++) {
            let dailyAdj = adjArray[i];
            if (dailyAdj === 0 || dailyAdj === undefined) continue;
            if (dailyAdj < 0) {
              let sortedAccts = [...adjustedAccounts].sort((a, b) => b.values[i] - a.values[i]);
              let remainingToDeduct = Math.abs(dailyAdj);
              for (let acct of sortedAccts) {
                if (remainingToDeduct <= 0) break;
                const userMin = minBalances[acct.name];
                const acctMin = (userMin !== undefined && userMin !== "") ? Number(userMin) : (200000 * (data.fxRates[cur] || 1));
                const availableToDeduct = acct.values[i] - acctMin;
                if (availableToDeduct > 0) {
                  let deductAmount = Math.min(availableToDeduct, remainingToDeduct);
                  acct.values[i] -= deductAmount;
                  remainingToDeduct -= deductAmount;
                }
              }
              if (remainingToDeduct > 0) {
                const master = adjustedAccounts.find(a => a.name.toUpperCase().includes("MASTER")) || adjustedAccounts[0];
                if (master) master.values[i] -= remainingToDeduct;
              }
            } else {
              let remainingToAdd = dailyAdj;
              // LOGIC FIX: Sorted most negative accounts first (ascending order)
              let negAccts = [...adjustedAccounts].filter(a => a.values[i] < 0).sort((a, b) => a.values[i] - b.values[i]);
              for (let acct of negAccts) {
                if (remainingToAdd <= 0) break;
                let fill = Math.min(remainingToAdd, -acct.values[i]);
                acct.values[i] += fill;
                remainingToAdd -= fill;
              }
              if (remainingToAdd > 0) {
                const master = adjustedAccounts.find(a => a.name.toUpperCase().includes("MASTER")) || adjustedAccounts[0];
                if (master) master.values[i] += remainingToAdd;
              }
            }
          }
        }

        newPools[poolKey] = { ...pool, accounts: adjustedAccounts, totals: adjustedPoolTotals };
        adjustedPoolTotals.forEach((v, i) => { newTotals[i] += v; });
      });
      const newStandalone = (curData.standalone || []).map(acct => {
        const adj = suggestionAdj[acct.name];
        const adjustedValues = adj ? acct.values.map((v, i) => v + (adj[i] || 0)) : acct.values;
        adjustedValues.forEach((v, i) => { newTotals[i] += v; });
        return { ...acct, values: adjustedValues };
      });
      adjusted[cur] = { ...curData, pools: newPools, standalone: newStandalone, totals: newTotals };
    });
    return adjusted;
  }, [detailHierarchy, suggestionAdj, applySuggestions, data, minBalances]);

  // Entity / Bank grouping over the same account values as the "All Accounts" view (displayHierarchy)
  const groupedByAttr = useMemo(() => {
    if (!data) return { entity: [], bank: [] };
    const attrOf = {};
    data.netData.forEach(a => { attrOf[a.name] = { entity: a.entity || "(blank)", bank: a.bank || "(blank)" }; });
    const toEur = (v, cur) => (cur === "EUR" || !data.fxRates[cur]) ? v : v / data.fxRates[cur];
    const accts = [];
    Object.entries(displayHierarchy).forEach(([cur, cd]) => {
      Object.values(cd.pools).forEach(p => p.accounts.forEach(a => accts.push({ ...a, cur })));
      (cd.standalone || []).forEach(a => accts.push({ ...a, cur }));
    });
    const build = attr => {
      const g = {};
      accts.forEach(a => {
        const k = attrOf[a.name]?.[attr] || "(blank)";
        if (!g[k]) g[k] = { name: k, accounts: [], totalsEur: data.dates.map(() => 0) };
        g[k].accounts.push(a);
        a.values.forEach((v, i) => { g[k].totalsEur[i] += toEur(v, a.cur); });
      });
      return Object.values(g).sort((x, y) => x.name.localeCompare(y.name));
    };
    return { entity: build("entity"), bank: build("bank") };
  }, [data, displayHierarchy]);

  const poolList = useMemo(() => {
    if (!data) return [];
    const pools = {};
    data.mapping.forEach(m => {
      const poolKey = (m.pool && m.pool.trim()) ? m.pool.trim() : m.sub;
      if (!pools[poolKey]) pools[poolKey] = { name: poolKey, master: m.master || "", ccy: m.ccy || "EUR", subs: [] };
      if (!pools[poolKey].subs.includes(m.sub)) pools[poolKey].subs.push(m.sub);
    });
	
	const exportCsv = (rows, name) => {
    const header = ["Type","Reason","From","To","Amount","Cur","TargetAmount","TargetCur","TradeDate","SettleDate"];
    const esc = v => `"${String(v ?? "").replace(/"/g,'""')}"`;
    const lines = [header.join(",")].concat(rows.map(s => [
      s.cur===s.targetCur?"SWEEP":"FX", s.reason, s.from, s.to, s.amount, s.cur, s.targetAmount, s.targetCur, s.date, s.settleDate
    ].map(esc).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
  };
  

	
return Object.values(pools).map(p => {
      const hasOD = Math.abs(data.facRows.find(r =>
        r.fac.toUpperCase().trim() === p.master.toUpperCase().trim() &&
        r.type.toUpperCase().trim() === "KREDIT FACILITET"
      )?.values[0] ?? 0) > 0;
      const fxRate = data.fxRates[p.ccy] || 1;
	  const autoMin = hasOD ? ((-Math.abs(data.facRows.find(r =>
  r.fac.toUpperCase().trim() === p.master.toUpperCase().trim() &&
  r.type.toUpperCase().trim() === "KREDIT FACILITET"
)?.values[0] ?? 0) + globalODMin) * fxRate) : (p.subs.length * 200000 * fxRate);
      const override = poolMinOverrides[p.name.toUpperCase().trim()];
      return { ...p, hasOD, autoMin, fxRate, override };
    }).sort((a, b) => a.ccy.localeCompare(b.ccy) || a.name.localeCompare(b.name));
  }, [data, poolMinOverrides, globalODMin]);
  
    const odMasterList = useMemo(() => {
  if (!data) return [];
  const out = {};
  data.facilityNames.forEach(fac => {
    const lim = Math.abs(data.facRows.find(r => r.fac.toUpperCase().trim() === fac.toUpperCase().trim() && r.type.toUpperCase().trim() === "KREDIT FACILITET")?.values[0] ?? 0);
    if (lim <= 0) return;
    const key = fac.toUpperCase().trim();
    const subs = data.mapping.filter(m => (m.master || "").toUpperCase().trim() === key).map(m => m.sub);
    out[key] = { master: fac, key, limit: lim, subs };
  });
  return Object.values(out).sort((a, b) => a.master.localeCompare(b.master));
}, [data]);
  
  const handleRefreshSuggestions = useCallback(() => {
    // Safely verify all required state exists before calculating
    if (!data || typeof detailHierarchy === "undefined") return;


const nextSugg = buildSuggestions(
  data.facRows,
  data.dates,
  data.facilityNames,
  odFacSet,
  data.mapping,
  data.fxRates,
  customTransfers,
  data.netData,
  minBalances,
  poolMinOverrides,
globalODMin,
  ccyPriority,
  intercompanyLines,
  globalTopUp
);
    
    setSuggestions(nextSugg);
    setSelectedSugg(new Set(nextSugg.map(suggKey)));
  }, [data, detailHierarchy, odFacSet, customTransfers, minBalances, poolMinOverrides, globalODMin, ccyPriority, intercompanyLines, globalTopUp]);

  useEffect(() => {
    if (data) {
      handleRefreshSuggestions();
    }
  }, [data, handleRefreshSuggestions]);
  
 if (!data) {
      return (
        <div style={{
          fontFamily:"'DM Mono',monospace",
          background:C.bg,
          minHeight:"100vh",
          color:C.text,
          padding:16,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          width: `${100 / scale}%`
        }}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
          <div>
            <div style={{fontSize:9,letterSpacing:4,color:C.accent,textTransform:"uppercase",marginBottom:2}}>Treasury Intelligence</div>
            <h1 style={{fontFamily:"'DM Sans'",fontSize:20,fontWeight:800,margin:0}}>Cash Optimizer</h1>
          </div>
          <label style={{cursor:"pointer",background:C.card,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 10px",fontSize:11,color:C.muted}}>
            📂 New file<input type="file" accept=".xlsx" style={{display:"none"}} onChange={e=>load(e.target.files[0])}/>
          </label>
        </div>
        <label style={{ display: "block", cursor: "pointer", padding: "72px 40px", textAlign: "center", background: C.card, borderRadius: 12, border: `2px dashed ${C.accent}40`, maxWidth: 720, margin: "8vh auto 0" }}>
          <input type="file" accept=".xlsx" style={{ display: "none" }} onChange={e => load(e.target.files[0])} />
          <div style={{ fontSize: 38, marginBottom: 14, opacity: 0.85 }}>↑</div>
          <div style={{ fontFamily: "'DM Sans'", fontSize: 19, fontWeight: 800, color: C.text, marginBottom: 8 }}>Load your liquidity workbook</div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, marginBottom: 22 }}>
            Drop in the Master workbook to build the position matrix, facility headroom<br />and the funding proposals for the rolling horizon.
          </div>
          <div style={{ display: "inline-block", padding: "10px 22px", background: `${C.accent}18`, border: `1px solid ${C.accent}`, borderRadius: 6, fontSize: 12, fontWeight: 800, color: C.accent, letterSpacing: 0.5 }}>
            Choose .xlsx file
          </div>
          <div style={{ marginTop: 26, paddingTop: 18, borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 24, fontSize: 10, color: C.muted, letterSpacing: 0.4 }}>
            <span>Required sheets</span>
            <span style={{ color: C.text }}>Net</span>
            <span style={{ color: C.text }}>Cash</span>
            <span style={{ color: C.text }}>Forecast</span>
            <span style={{ color: C.text }}>Total overview</span>
            <span style={{ color: C.text }}>OD_Struktur</span>
            <span style={{ color: C.text }}>FX</span>
          </div>
        </label>
      </div>
    );
  }

  return (
    <div style={{fontFamily:"'DM Mono',monospace",background:C.bg,minHeight:"100vh",color:C.text,padding:16}}>
      {/* ── HEADER ── */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
        <div>
          <div style={{fontSize:9,letterSpacing:4,color:C.accent,textTransform:"uppercase",marginBottom:2}}>Treasury Intelligence</div>
          <h1 style={{fontFamily:"'DM Sans'",fontSize:20,fontWeight:800,margin:0}}>Cash Optimizer</h1>
          <div style={{fontSize:10,color:C.muted,marginTop:1}}>{data.facilityNames.length} facilities · {suggestions.length} actionable execution suggestions</div>
        </div>
        <label style={{cursor:"pointer",background:C.card,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 10px",fontSize:11,color:C.muted}}>📂 New file<input type="file" accept=".xlsx" style={{display:"none"}} onChange={e=>load(e.target.files[0])}/></label>
      </div>

<div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
       
        {/* LEFT PRIMARY PANEL CONTAINER */}
        <div style={{ flex: 1, minWidth: 600 }}>
		<div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 12, overflow: "hidden" }}>
            <VisualFacilityMonitor 
  data={data} 
  fv={fv} 
  collapsed={false} 
  onToggle={() => {}} 
/>
          </div>
          <div style={{display:"flex",gap:2,marginBottom:12,borderBottom:`1px solid ${C.border}`}}>
{[["positions","Positions Matrix"], ["facilities","Facility Limits"], ["rules","Rules Configuration"], ["transfers","Manual Adjustments"], ["cashpools","Structural Trees"], ["poolrules","Pool Min. Rules"], ["fxpriority","FX Source Priority"], ["cashpos","Cash Position"], ["export","Export"], ["forecast","Liquidity Forecast"], ["dataquality","Data Quality"], ["assumptions","Assumptions & Audit"]].map(([t, label]) => (
              <button key={t} onClick={() => setTab(t)} style={{background:"none",border:"none",cursor:"pointer",padding:"8px 12px",color:tab===t?C.text:C.muted,fontFamily:"'DM Sans'",fontWeight:600,fontSize:12,borderBottom:tab===t?`2px solid ${C.accent}`:"2px solid transparent"}}>{label}</button>
            ))}
          </div>

          {tab === "positions" && (
            <div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {[["top", "Cash Pool Masters"],["pool", "Cash Pools"],["detail", "All Accounts"],["entity", "By Entity"],["bank", "By Bank"]].map(([v, l]) => (
                    <button key={v} onClick={() => setPosView(v)} style={{padding: "4px 10px",borderRadius: 4,cursor: "pointer",fontSize: 11,fontFamily: "'DM Sans'",fontWeight: 600,background: posView === v ? C.accent : C.card,color: posView === v ? "#fff" : C.muted,border: `1px solid ${posView === v ? C.accent : C.border}`}}>{l}</button>
                  ))}
                </div>
              </div>

              <div style={{background:C.card,borderRadius:8,border:`1px solid ${C.border}`,overflow:"auto",maxHeight:"78vh"}}>
<table style={{width:"100%",borderCollapse:"collapse",fontSize:10}}>
                  <thead style={{position:"sticky",top:0,zIndex:10,background:C.card,boxShadow:`0 1px 0 ${C.border}`}}>
                    <tr>
                      <TH l={posView === "top" ? "Cash Pool Master" : "Group / Account"} rs={posView === "top" ? 1 : 2} />
                      {posView === "top" && <TH l="Metric" />}
                      {data.dates.slice(0, 7).map(d => posView === "top" ? <TH key={d} l={d} r hi /> : <TH key={d} l={d} center hi cs={2} />)}
                    </tr>
                    {posView !== "top" && (
                      <tr>
                        {data.dates.slice(0, 7).map(d => (
                          <React.Fragment key={d}>
                            <th style={{padding:"4px 8px",textAlign:"right",color:C.muted,fontSize:9,background:"#ffffff03",borderTop:`1px solid ${C.border}30`}}>LOCAL</th>
                            <th style={{padding:"4px 8px",textAlign:"right",color:C.muted,fontSize:9,background:"#ffffff03",borderTop:`1px solid ${C.border}30`}}>EUR</th>
                          </React.Fragment>
                        ))}
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {posView === "top" && data.facilityNames.map(fac => {
                      const isExp = expandedFacs.has(fac);
                      const subMapping = detailByMaster[fac.toUpperCase().trim()] || [];
                      return (
                        <React.Fragment key={fac}>
                          {["Expected", "Kredit facilitet", "Headroom"].map((m, idx) => {
                            const label = m === "Kredit facilitet" ? "OD Limit" : m;
                            return (
                              <tr key={m} style={{ background: idx === 0 ? "rgba(56, 189, 248, 0.03)" : "transparent", borderBottom: idx === 2 ? `3px solid ${C.bg}` : `1px solid ${C.border}15` }}>
                                {idx === 0 && (
                                  <td rowSpan={isExp ? 3 + subMapping.length : 3} style={{padding:"10px 8px", fontWeight:800, verticalAlign:"top", color:C.text, borderRight:`1px solid ${C.border}30`, fontSize:10}}>
                                    {fac}
                                  </td>
                                )}
                                <td onClick={() => idx === 0 && setExpandedFacs(p => { const n = new Set(p); n.has(fac) ? n.delete(fac) : n.add(fac); return n; })} style={{padding:"6px 8px", color: m === "Headroom" ? C.accent : (idx === 1 ? C.muted : C.text), cursor:idx===0?"pointer":"default", fontWeight: 700}}>
                                  {idx === 0 ? (isExp ? "▼ Expected" : "▶ Expected") : label}
                                </td>
                                {data.dates.slice(0, 7).map((_, di) => {
                                  const val = fv(fac, m, di);
                                  const isBreach = m === "Headroom" && val < 0;
                                  const isNearLimit = m === "Headroom" && val >= 0 && val < (Math.abs(fv(fac, "Kredit facilitet", di)) * 0.15);
                                  return (
                                    <td key={di} style={{ padding: "6px 8px", textAlign: "right", fontWeight: m === "Headroom" ? 800 : 400, color: val < 0 ? C.neg : (m === "Headroom" ? C.pos : (m === "Kredit facilitet" ? C.muted : C.text)), background: isBreach ? "rgba(239, 68, 68, 0.10)" : (isNearLimit ? "rgba(245, 158, 11, 0.05)" : "transparent"), borderLeft: `1px solid ${C.border}10` }}>
                                      {fmt(val)}
                                      {m === "Headroom" && <div style={{ height: 2, width: "100%", background: isBreach ? C.neg : (isNearLimit ? C.warn : C.pos), marginTop: 2, borderRadius: 1 }} />}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
{isExp && subMapping.map((subM, sIdx) => {
  const netRow = data.netData.find(n => n.name.toUpperCase().trim() === subM.sub.toUpperCase().trim());
  const cur = netRow?.cur || "EUR";
  const isAcctExp = expAccounts.has("top_" + subM.sub);
  
  return (
    <React.Fragment key={subM.sub}>
      <tr style={{borderBottom: (sIdx === subMapping.length - 1 && !isAcctExp) ? `2px solid ${C.border}` : `1px solid ${C.border}05`, background:"rgba(255,255,255,0.01)"}}>
        <td style={{padding:"6px 8px", paddingLeft:20, color:C.muted, fontSize:10, position:"relative"}}>
          <span onClick={() => setExpAccounts(p => { const n = new Set(p); isAcctExp ? n.delete("top_" + subM.sub) : n.add("top_" + subM.sub); return n; })} style={{ cursor: "pointer", color: C.accent, marginRight: 5, fontSize: 9, userSelect: "none", fontWeight: 700 }}>
            {isAcctExp ? "[-]" : "[+]"}
          </span>
          <span style={{fontStyle:"italic"}}>{subM.sub}</span>
        </td>
        {data.dates.slice(0, 7).map((_, di) => { 
          let rawVal = netRow?.values ? netRow.values[di] : 0; 
          if (applySuggestions && netRow?.name) {
              const poolNameKey = (subM.pool && subM.pool.trim()) ? subM.pool.trim() : netRow.name;
              const treeCurData = displayHierarchy[cur];
              if (treeCurData && treeCurData.pools[poolNameKey]) {
                  const waterfallAcct = treeCurData.pools[poolNameKey].accounts.find(a => a.name === netRow.name);
                  if (waterfallAcct) rawVal = waterfallAcct.values[di];
              }
          }
          return <td key={di} style={{padding:"6px 8px", textAlign:"right", fontSize:10, color: rawVal < 0 ? C.neg : C.muted, borderLeft: `1px solid ${C.border}10`}}>{fmt(getEurVal(rawVal, cur))}</td>; 
        })}
      </tr>
      {isAcctExp && ["Forecast", "Cash"].map(type => {
        const vals = (type === "Forecast" ? data.forecastData : data.cashData)[subM.sub] || data.dates.map(() => 0);
        const tColor = type === "Forecast" ? C.accent : C.pos;
        return (
          <tr key={`${subM.sub}_${type}`} style={{ background: `${tColor}05`, borderBottom: (type === "Cash" && sIdx === subMapping.length - 1) ? `2px solid ${C.border}` : `1px solid ${C.border}05` }}>
            <td style={{ padding: "4px 8px", paddingLeft: 40, fontSize: 9, color: tColor, fontStyle: "italic" }}>↳ {type}</td>
            {data.dates.slice(0, 7).map((_, di) => (
              <td key={di} style={{ padding: "4px 8px", textAlign: "right", fontSize: 9, color: vals[di] < 0 ? C.neg : (vals[di] !== 0 ? tColor : C.muted), borderLeft: `1px solid ${C.border}10` }}>
                {fmt(getEurVal(vals[di], cur))}
              </td>
            ))}
          </tr>
        );
      })}
    </React.Fragment>
  );
})}
</React.Fragment>
                      );
                    })}
					
                  {posView === "pool" && Object.entries(displayHierarchy).sort((a,b)=>a[0].localeCompare(b[0])).map(([cur, curData]) => {
                    const isCurExp = expCur.has(cur);
                    return (
                      <React.Fragment key={"pool_"+cur}>
                        <tr style={{background:"#1e293b",borderBottom:`1px solid ${C.border}10`}}>
                          <td style={{padding:"10px 8px",fontWeight:800,cursor:"pointer",color:C.text}} onClick={()=>setExpCur(p=>{const n=new Set(p);isCurExp?n.delete(cur):n.add(cur);return n;})}>
                            {isCurExp?"▼":"▶"} {cur} TOTAL
                            <span onClick={(e)=>{e.stopPropagation();setExpPoolDetails(p=>{const n=new Set(p);const k="cur_"+cur;n.has(k)?n.delete(k):n.add(k);return n;});}} style={{cursor:"pointer",color:C.accent,marginLeft:8,fontSize:10,fontWeight:800}}>
                              {expPoolDetails.has("cur_"+cur)?"[-]":"[+]"}
                            </span>
                          </td>
                          {curData.totals.slice(0,7).map((v,i)=>(
                            <React.Fragment key={i}>
                              <td style={{padding:"6px 8px",textAlign:"right",fontWeight:800,color:v<0?C.neg:(v>0?C.pos:C.text)}}>{fmt(v)}</td>
                              <td style={{padding:"6px 8px",textAlign:"right",fontWeight:800,color:cur==="EUR"?C.text:C.muted}}>{fmt(getEurVal(v,cur))}</td>
                            </React.Fragment>
                          ))}
                        </tr>
                        {expPoolDetails.has("cur_"+cur) && ["Forecast","Cash"].map(type => {
                          const allAccts = Object.values(curData.pools).flatMap(p => p.accounts).concat(curData.standalone || []);
                          const vals = data.dates.map((_, i) => allAccts.reduce((sum, a) => sum + ((type === "Forecast" ? data.forecastData : data.cashData)[a.name]?.[i] || 0), 0));
                          const tColor = type === "Forecast" ? C.accent : C.pos;
                          return (
                            <tr key={`cur_agg_${type}`} style={{ background: `${tColor}10`, borderBottom: `1px solid ${C.border}20` }}>
                              <td style={{ padding: "4px 8px", paddingLeft: 24, fontSize: 9, color: tColor, fontStyle: "italic", fontWeight: 700 }}>↳ {cur} Total {type}</td>
                              {vals.slice(0, 7).map((v, i) => (
                                <React.Fragment key={i}>
                                  <td style={{ padding: "4px 8px", textAlign: "right", fontSize: 9, color: v < 0 ? C.neg : (v !== 0 ? tColor : C.muted), fontWeight: 700 }}>{fmt(v)}</td>
                                  <td style={{ padding: "4px 8px", textAlign: "right", fontSize: 9, color: C.muted }}>{fmt(getEurVal(v, cur))}</td>
                                </React.Fragment>
                              ))}
                            </tr>
                          );
                        })}
                      {isCurExp && Object.values(curData.pools).sort((a, b) => a.name.localeCompare(b.name)).map(pool => {
  const isPoolExp = expPoolCur.has(pool.name);
  const isAggExp = expPoolDetails.has(pool.name);
  return (
    <React.Fragment key={pool.name}>
      <tr style={{ background: "rgba(56, 189, 248, 0.02)", borderBottom: `1px solid ${C.border}40` }}>
        <td style={{ padding: "8px 8px", paddingLeft: 24, fontWeight: 700, cursor: "pointer", color: C.text }} onClick={() => setExpPoolCur(p => { const n = new Set(p); isPoolExp ? n.delete(pool.name) : n.add(pool.name); return n; })}>
          {isPoolExp ? "▼" : "▶"} {pool.name} <span style={{ fontSize: 8, color: C.muted, marginLeft: 4, background: `${C.border}40`, padding: "1px 3px", borderRadius: 3 }}>Pool</span>
          <span onClick={(e) => { e.stopPropagation(); setExpPoolDetails(p => { const n = new Set(p); isAggExp ? n.delete(pool.name) : n.add(pool.name); return n; })}} style={{ cursor: "pointer", color: C.accent, marginLeft: 8, fontSize: 10, fontWeight: 800 }}>
            {isAggExp ? "[-]" : "[+]"}
          </span>
        </td>
        {pool.totals.slice(0, 7).map((v, i) => (
          <React.Fragment key={i}>
            <td style={{ padding: "8px 8px", textAlign: "right", fontWeight: 700, color: v < 0 ? C.neg : C.text }}>{fmt(v)}</td>
            <td style={{ padding: "8px 8px", textAlign: "right", fontWeight: 700, color: cur === "EUR" ? C.text : C.muted }}>{fmt(getEurVal(v, cur))}</td>
          </React.Fragment>
        ))}
      </tr>

      {isAggExp && ["Forecast", "Cash"].map(type => {
        const poolVals = data.dates.map((_, i) => pool.accounts.reduce((sum, a) => sum + ((type === "Forecast" ? data.forecastData : data.cashData)[a.name]?.[i] || 0), 0));
        const tColor = type === "Forecast" ? C.accent : C.pos;
        return (
          <tr key={`pool_agg_${type}`} style={{ background: `${tColor}10`, borderBottom: `1px solid ${C.border}20` }}>
            <td style={{ padding: "4px 8px", paddingLeft: 32, fontSize: 9, color: tColor, fontStyle: "italic", fontWeight: 700 }}>↳ Total {type}</td>
            {poolVals.slice(0, 7).map((v, i) => (
              <React.Fragment key={i}>
                <td style={{ padding: "4px 8px", textAlign: "right", fontSize: 9, color: v < 0 ? C.neg : (v !== 0 ? tColor : C.muted), fontWeight: 700 }}>{fmt(v)}</td>
                <td style={{ padding: "4px 8px", textAlign: "right", fontSize: 9, color: C.muted }}>{fmt(getEurVal(v, cur))}</td>
              </React.Fragment>
            ))}
          </tr>
        );
      })}

{isPoolExp && pool.accounts.map((a, sIdx) => {
  const isAcctExp = expAccounts.has("dtl_" + a.name);
  return (
    <React.Fragment key={a.name}>
      <tr style={{ borderBottom: sIdx === pool.accounts.length - 1 ? `2px solid ${C.border}` : `1px solid ${C.border}05`, background: "#ffffff01" }}>
        <td style={{ padding: "6px 8px", paddingLeft: 32, color: "#94a3b8", fontSize: 10, position: "relative" }}>
          <div style={{ position: "absolute", left: 22, top: 0, bottom: sIdx === pool.accounts.length - 1 ? "50%" : 0, width: 1, background: `${C.border}` }} />
          <div style={{ position: "absolute", left: 22, top: "50%", width: 6, height: 1, background: `${C.border}` }} />
          <span onClick={() => setExpAccounts(p => { const n = new Set(p); isAcctExp ? n.delete("dtl_" + a.name) : n.add("dtl_" + a.name); return n; })} style={{ cursor: "pointer", color: C.accent, marginRight: 5, fontSize: 9, userSelect: "none", fontWeight: 700 }}>
            {isAcctExp ? "[-]" : "[+]"}
          </span>
          {a.name}
        </td>
        {a.values.slice(0, 7).map((v, i) => (
          <React.Fragment key={i}>
            <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 10, color: v < 0 ? C.neg : C.text }}>{fmt(v)}</td>
            <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 10, color: cur === "EUR" ? C.text : C.muted }}>{fmt(getEurVal(v, cur))}</td>
          </React.Fragment>
        ))}
      </tr>
      {isAcctExp && ["Forecast", "Cash"].map(type => {
        const vals = (type === "Forecast" ? data.forecastData : data.cashData)[a.name] || data.dates.map(() => 0);
        const tColor = type === "Forecast" ? C.accent : C.pos;
        return (
          <tr key={type} style={{ background: `${tColor}08`, borderBottom: `1px solid ${C.border}05` }}>
            <td style={{ padding: "4px 8px", paddingLeft: 48, fontSize: 9, color: tColor, fontStyle: "italic" }}>↳ {type}</td>
            {vals.slice(0, 7).map((v, i) => (
              <React.Fragment key={i}>
                <td style={{ padding: "4px 8px", textAlign: "right", fontSize: 9, color: v < 0 ? C.neg : (v !== 0 ? tColor : C.muted) }}>{fmt(v)}</td>
                <td style={{ padding: "4px 8px", textAlign: "right", fontSize: 9, color: C.muted }}>{fmt(getEurVal(v, cur))}</td>
              </React.Fragment>
            ))}
          </tr>
        );
      })}
    </React.Fragment>
  );
})}
 </React.Fragment>
  );
})}
{isCurExp && (curData.standalone || []).map(a => (
  <tr key={"sa_pool_"+a.name} style={{ borderBottom: `1px solid ${C.border}05`, background: "#ffffff01" }}>
    <td style={{ padding: "6px 8px", paddingLeft: 24, color: C.muted, fontSize: 10 }}>
      {a.name} <span style={{ fontSize: 8, color: C.warn, marginLeft: 4, background: `${C.warn}20`, padding: "1px 3px", borderRadius: 3 }}>Unmapped</span>
    </td>
    {a.values.slice(0, 7).map((v, i) => (
      <React.Fragment key={i}>
        <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 10, color: v < 0 ? C.neg : C.text }}>{fmt(v)}</td>
        <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 10, color: cur === "EUR" ? C.text : C.muted }}>{fmt(getEurVal(v, cur))}</td>
      </React.Fragment>
    ))}
  </tr>
))}
                      </React.Fragment>
                    );
                  })}
                    {posView === "detail" && Object.entries(displayHierarchy).sort((a,b)=>a[0].localeCompare(b[0])).map(([cur, curData]) => {
                      const isCurExp = expCur.has("dtl_"+cur);
                      return (
                        <React.Fragment key={"dtl_"+cur}>
                          <tr style={{background:"#1e293b",borderBottom:`1px solid ${C.border}10`}}>
                            <td style={{padding:"10px 8px",fontWeight:800,cursor:"pointer",color:C.text}} onClick={() => setExpCur(p => { const n = new Set(p); isCurExp ? n.delete("dtl_"+cur) : n.add("dtl_"+cur); return n; })}>
                              {isCurExp ? "▼" : "▶"} {cur} TOTAL
                              <span onClick={(e)=>{e.stopPropagation();setExpPoolDetails(p=>{const n=new Set(p);const k="dtlcur_"+cur;n.has(k)?n.delete(k):n.add(k);return n;});}} style={{cursor:"pointer",color:C.accent,marginLeft:8,fontSize:10,fontWeight:800}}>
                                {expPoolDetails.has("dtlcur_"+cur)?"[-]":"[+]"}
                              </span>
                            </td>
                            {curData.totals.slice(0, 7).map((v, i) => (
                              <React.Fragment key={i}>
                                <td style={{padding:"6px 8px",textAlign:"right",fontWeight:800,color:v<0?C.neg:(v>0?C.pos:C.text)}}>{fmt(v)}</td>
                                <td style={{padding:"6px 8px",textAlign:"right",fontWeight:800,color:cur==="EUR"?C.text:C.muted}}>{fmt(getEurVal(v,cur))}</td>
                              </React.Fragment>
                            ))}
                          </tr>
{expPoolDetails.has("dtlcur_"+cur) && ["Forecast","Cash"].map(type => {
  const allAccts = Object.values(curData.pools).flatMap(p => p.accounts).concat(curData.standalone || []);
  const vals = data.dates.map((_, i) => allAccts.reduce((s, a) => s + ((type === "Forecast" ? data.forecastData : data.cashData)[a.name]?.[i] || 0), 0));
  const tColor = type === "Forecast" ? C.accent : C.pos;
  return (
    <tr key={`dtlcur_agg_${type}`} style={{ background: `${tColor}10`, borderBottom: `1px solid ${C.border}20` }}>
      <td style={{ padding: "4px 8px", paddingLeft: 20, fontSize: 9, color: tColor, fontStyle: "italic", fontWeight: 700 }}>↳ {cur} Total {type}</td>
      {vals.slice(0, 7).map((v, i) => (
        <React.Fragment key={i}>
          <td style={{ padding: "4px 8px", textAlign: "right", fontSize: 9, color: v < 0 ? C.neg : (v !== 0 ? tColor : C.muted), fontWeight: 700 }}>{fmt(v)}</td>
          <td style={{ padding: "4px 8px", textAlign: "right", fontSize: 9, color: C.muted }}>{fmt(getEurVal(v, cur))}</td>
        </React.Fragment>
      ))}
    </tr>
  );
})}
{isCurExp && Object.values(curData.pools).sort((a, b) => a.name.localeCompare(b.name)).map((pool) => {
  const isPoolExp = expPoolCur.has("dtl_" + pool.name);
  const isAggExp = expPoolDetails.has("dtl_" + pool.name);
  return (
    <React.Fragment key={pool.name}>
      <tr style={{ background: "#ffffff05", borderBottom: `1px solid ${C.border}05` }}>
        <td style={{ padding: "8px 8px", paddingLeft: 20, fontWeight: 700, cursor: "pointer", color: C.text }} onClick={() => setExpPoolCur(p => { const n = new Set(p); isPoolExp ? n.delete("dtl_" + pool.name) : n.add("dtl_" + pool.name); return n; })}>
          {isPoolExp ? "▼" : "▶"} {pool.name} <span style={{ fontSize: 8, color: C.muted, marginLeft: 4, background: `${C.border}40`, padding: "1px 3px", borderRadius: 3 }}>Pool</span>
          <span onClick={(e) => { e.stopPropagation(); setExpPoolDetails(p => { const n = new Set(p); isAggExp ? n.delete("dtl_" + pool.name) : n.add("dtl_" + pool.name); return n; }) }} style={{ cursor: "pointer", color: C.accent, marginLeft: 8, fontSize: 10, fontWeight: 800 }}>
            {isAggExp ? "[-]" : "[+]"}
          </span>
        </td>
        {pool.totals.slice(0, 7).map((v, i) => (
          <React.Fragment key={i}>
            <td style={{ padding: "8px 8px", textAlign: "right", fontWeight: 700, color: v < 0 ? C.neg : (v > 0 ? C.pos : C.text) }}>{fmt(v)}</td>
            <td style={{ padding: "8px 8px", textAlign: "right", fontWeight: 700, color: cur === "EUR" ? C.text : C.muted }}>{fmt(getEurVal(v, cur))}</td>
          </React.Fragment>
        ))}
      </tr>

      {isAggExp && ["Forecast", "Cash"].map(type => {
        const poolVals = data.dates.map((_, i) => pool.accounts.reduce((sum, a) => sum + ((type === "Forecast" ? data.forecastData : data.cashData)[a.name]?.[i] || 0), 0));
        const tColor = type === "Forecast" ? C.accent : C.pos;
        return (
          <tr key={`dtl_agg_${type}`} style={{ background: `${tColor}10`, borderBottom: `1px solid ${C.border}20` }}>
            <td style={{ padding: "4px 8px", paddingLeft: 32, fontSize: 9, color: tColor, fontStyle: "italic", fontWeight: 700 }}>↳ Total {type}</td>
            {poolVals.slice(0, 7).map((v, i) => (
              <React.Fragment key={i}>
                <td style={{ padding: "4px 8px", textAlign: "right", fontSize: 9, color: v < 0 ? C.neg : (v !== 0 ? tColor : C.muted), fontWeight: 700 }}>{fmt(v)}</td>
                <td style={{ padding: "4px 8px", textAlign: "right", fontSize: 9, color: C.muted }}>{fmt(getEurVal(v, cur))}</td>
              </React.Fragment>
            ))}
          </tr>
        );
      })}

      {isPoolExp && pool.accounts.map((a, sIdx) => {
        const isAcctExp = expAccounts.has("dtl_" + a.name);
        return (
          <React.Fragment key={a.name}>
            <tr style={{ borderBottom: sIdx === pool.accounts.length - 1 ? `2px solid ${C.border}` : `1px solid ${C.border}05`, background: "#ffffff01" }}>
              <td style={{ padding: "6px 8px", paddingLeft: 32, color: "#94a3b8", fontSize: 10, position: "relative" }}>
                <div style={{ position: "absolute", left: 22, top: 0, bottom: sIdx === pool.accounts.length - 1 ? "50%" : 0, width: 1, background: `${C.border}` }} />
                <div style={{ position: "absolute", left: 22, top: "50%", width: 6, height: 1, background: `${C.border}` }} />
                <span onClick={() => setExpAccounts(p => { const n = new Set(p); isAcctExp ? n.delete("dtl_" + a.name) : n.add("dtl_" + a.name); return n; })} style={{ cursor: "pointer", color: C.accent, marginRight: 5, fontSize: 9, userSelect: "none", fontWeight: 700 }}>{isAcctExp ? "[-]" : "[+]"}</span>
                {a.name}
              </td>
              {a.values.slice(0, 7).map((v, i) => (
                <React.Fragment key={i}>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 10, color: v < 0 ? C.neg : C.text }}>{fmt(v)}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 10, color: cur === "EUR" ? C.text : C.muted }}>{fmt(getEurVal(v, cur))}</td>
                </React.Fragment>
              ))}
            </tr>
            {isAcctExp && ["Forecast", "Cash"].map(type => {
              const vals = (type === "Forecast" ? data.forecastData : data.cashData)[a.name] || data.dates.map(() => 0);
              const tColor = type === "Forecast" ? C.accent : C.pos;
              return (
                <tr key={type} style={{ background: `${tColor}08`, borderBottom: `1px solid ${C.border}05` }}>
                  <td style={{ padding: "4px 8px", paddingLeft: 48, fontSize: 9, color: tColor, fontStyle: "italic" }}>↳ {type}</td>
                  {vals.slice(0, 7).map((v, i) => (
                    <React.Fragment key={i}>
                      <td style={{ padding: "4px 8px", textAlign: "right", fontSize: 9, color: v < 0 ? C.neg : (v !== 0 ? tColor : C.muted) }}>{fmt(v)}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right", fontSize: 9, color: C.muted }}>{fmt(getEurVal(v, cur))}</td>
                    </React.Fragment>
                  ))}
                </tr>
              );
            })}
          </React.Fragment>
        );
      })}
    </React.Fragment>
  );
})}
{isCurExp && (curData.standalone || []).map(a => (
  <tr key={"sa_dtl_"+a.name} style={{ borderBottom: `1px solid ${C.border}05`, background: "#ffffff01" }}>
    <td style={{ padding: "6px 8px", paddingLeft: 20, color: C.muted, fontSize: 10 }}>
      {a.name} <span style={{ fontSize: 8, color: C.warn, marginLeft: 4, background: `${C.warn}20`, padding: "1px 3px", borderRadius: 3 }}>Unmapped</span>
    </td>
    {a.values.slice(0, 7).map((v, i) => (
      <React.Fragment key={i}>
        <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 10, color: v < 0 ? C.neg : C.text }}>{fmt(v)}</td>
        <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 10, color: cur === "EUR" ? C.text : C.muted }}>{fmt(getEurVal(v, cur))}</td>
      </React.Fragment>
    ))}
  </tr>
))}
                        </React.Fragment>
                      );
                    })}
                    {(posView === "entity" || posView === "bank") && groupedByAttr[posView].map(g => {
                      const k = posView + "_" + g.name;
                      const isExp = expCur.has(k);
                      return (
                        <React.Fragment key={k}>
                          <tr style={{ background: "#1e293b", borderBottom: `1px solid ${C.border}10` }}>
                            <td style={{ padding: "10px 8px", fontWeight: 800, cursor: "pointer", color: C.text }} onClick={() => setExpCur(p => { const n = new Set(p); isExp ? n.delete(k) : n.add(k); return n; })}>
                              {isExp ? "▼" : "▶"} {g.name} <span style={{ fontSize: 8, color: C.muted, marginLeft: 4, background: `${C.border}40`, padding: "1px 3px", borderRadius: 3 }}>{g.accounts.length} acct{g.accounts.length !== 1 ? "s" : ""}</span>
                            </td>
                            {g.totalsEur.slice(0, 7).map((v, i) => (
                              <React.Fragment key={i}>
                                <td style={{ padding: "6px 8px", textAlign: "right", color: C.muted }}>—</td>
                                <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 800, color: v < 0 ? C.neg : (v > 0 ? C.pos : C.text) }}>{fmt(v)}</td>
                              </React.Fragment>
                            ))}
                          </tr>
                          {isExp && g.accounts.map(a => (
                            <tr key={k + a.name} style={{ borderBottom: `1px solid ${C.border}05`, background: "#ffffff01" }}>
                              <td style={{ padding: "6px 8px", paddingLeft: 24, color: "#94a3b8", fontSize: 10 }}>{a.name} <span style={{ fontSize: 8, color: C.muted, marginLeft: 4 }}>{a.cur}</span></td>
                              {a.values.slice(0, 7).map((v, i) => (
                                <React.Fragment key={i}>
                                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 10, color: v < 0 ? C.neg : C.text }}>{fmt(v)}</td>
                                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 10, color: a.cur === "EUR" ? C.text : C.muted }}>{fmt(getEurVal(v, a.cur))}</td>
                                </React.Fragment>
                              ))}
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab==="facilities" && (
            <div style={{display:"flex",flexDirection:"column",gap:12,maxHeight:"78vh",overflowY:"auto"}}>
              {data.facilityNames.map(fac => (
                <div key={fac} style={{background:C.card,borderRadius:8,border:`1px solid ${C.border}`,overflow:"hidden"}}>
                  <div style={{padding:10,fontWeight:700,fontSize:12,borderBottom:`1px solid ${C.border}`}}>{fac}</div>
                  <table style={{width:"100%",fontSize:11,borderCollapse:"collapse"}}>
                    <tbody>
                      {["Opening","Net movements","Expected","Kredit facilitet","Headroom","Utilisation %"].map(type => (
                        <tr key={type} style={{borderBottom:`1px solid ${C.border}05`}}>
                          <td style={{padding:6,color:type==="Kredit facilitet"?C.accent:C.muted}}>{type==="Kredit facilitet"?"OD Limit":type}</td>
                          {data.dates.slice(0,7).map((_,i) => {
                            const v = fv(fac,type,i);
                            return <td key={i} style={{padding:6,textAlign:"right",color:(type === "Headroom" && v < 0) ? C.neg : C.text}}>{type === "Utilisation %" ? `${(v * 100).toFixed(1)}%` : fmt(v)}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}

          {tab==="rules" && (
            <div style={{background:C.card, borderRadius:8, border:`1px solid ${C.border}`, padding:16}}>
              <h2 style={{marginTop:0, marginBottom:6, fontSize:15, fontWeight:800, color:C.text}}>Treasury Rules & Limits</h2>
              <p style={{color:C.muted, fontSize:12, marginBottom:14}}>Configure account-specific overdraft permissions and explicit liquidity retention buffers.</p>
              <input type="text" placeholder="Search accounts..." value={ruleSearch} onChange={e => setRuleSearch(e.target.value)} style={{...F, marginBottom:12, maxWidth:260}} />
              <div style={{maxHeight: 400, overflowY:"auto", border:`1px solid ${C.border}`, borderRadius:6}}>
                <table style={{width:"100%", borderCollapse:"collapse", fontSize:11}}>
                  <thead style={{position:"sticky", top:0, background:C.card, boxShadow:`0 1px 0 ${C.border}`, zIndex:5}}>
                    <tr>
                      <th style={{padding:"8px 12px", textAlign:"left", color:C.muted, fontSize:9, textTransform:"uppercase"}}>Account</th>
                      <th style={{padding:"8px 12px", textAlign:"center", color:C.muted, fontSize:9, textTransform:"uppercase"}}>OD Access</th>
                      <th style={{padding:"8px 12px", textAlign:"right", color:C.muted, fontSize:9, textTransform:"uppercase"}}>Min Balance (Local)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.netData.filter(a => a.name.toLowerCase().includes(ruleSearch.toLowerCase())).map((a, idx) => {
                      const defaultMinLocal = Math.round(200000 * (data.fxRates[a.cur] || 1));
                      return (
                        <tr key={a.name} style={{borderBottom: `1px solid ${C.border}30`, background: idx % 2 === 0 ? "transparent" : "#ffffff01"}}>
                          <td style={{padding:"8px 12px"}}>
                            <div style={{fontWeight:600, color:C.text}}>{a.name}</div>
                            <div style={{color:C.muted, fontSize:9, marginTop:1}}>{a.cur} · Default: {fmt(defaultMinLocal)}</div>
                          </td>
                          <td style={{padding:"8px 12px", textAlign:"center"}}>
                            <input type="checkbox" checked={odAccess[a.name] || false} onChange={e => setOdAccess({...odAccess, [a.name]: e.target.checked})} style={{cursor:"pointer"}} />
                          </td>
                          <td style={{padding:"8px 12px", textAlign:"right"}}>
                            <input type="number" placeholder={`${fmt(defaultMinLocal)}`} value={minBalances[a.name] !== undefined ? minBalances[a.name] : ""} onChange={e => setMinBalances({...minBalances, [a.name]: e.target.value})} style={{...F, width: 130, padding: "4px 8px", textAlign:"right", fontWeight:600, color:C.accent}} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab==="transfers" && (
            <div style={{display:"flex", flexDirection:"column", gap:12}}>
              <div style={{background:C.card, borderRadius:8, border:`1px solid ${C.border}`, padding:16}}>
                <h2 style={{marginTop:0, marginBottom:12, fontSize:14, fontWeight:700}}>Create Custom Transfer</h2>
                <div style={{display:"flex", gap:10, alignItems:"flex-end"}}>
                  <div style={{flex:1}}>
                    <label style={{display:"block", fontSize:10, color:C.muted, marginBottom:4}}>From Account</label>
                    <select value={tfFrom} onChange={e=>setTfFrom(e.target.value)} style={F}>
                      <option value="">Select...</option>
                      {transferOptions.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div style={{flex:1}}>
                    <label style={{display:"block", fontSize:10, color:C.muted, marginBottom:4}}>To Account</label>
                    <select value={tfTo} onChange={e=>setTfTo(e.target.value)} style={F}>
                      <option value="">Select...</option>
                      {transferOptions.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div style={{flex:1}}>
                    <label style={{display:"block", fontSize:10, color:C.muted, marginBottom:4}}>Amount (Local)</label>
                    <input type="number" value={tfAmt} onChange={e=>setTfAmt(e.target.value)} placeholder="e.g. 500000" style={F} />
                  </div>
                  <button onClick={() => { if(tfFrom && tfTo && tfAmt) { setCustomTransfers([...customTransfers, {from:tfFrom, to:tfTo, amount:Number(tfAmt)}]); setTfFrom(""); setTfTo(""); setTfAmt(""); } }} style={{background:C.accent, border:"none", color:"#fff", padding:"8px 16px", borderRadius:6, cursor:"pointer", fontWeight:600, fontFamily:"'DM Sans'", fontSize:11}}>Add Transfer</button>
                </div>
              </div>
              {customTransfers.length > 0 && (
                <div style={{background:C.card, borderRadius:8, border:`1px solid ${C.border}`, padding:16}}>
                  <h3 style={{marginTop:0, marginBottom:12, fontSize:12, fontWeight:700}}>Scheduled Adjustments</h3>
                  {customTransfers.map((tf, i) => (
                    <div key={i} style={{display:"flex", justifyContent:"space-between", padding:"8px 12px", background:"#ffffff02", borderRadius:6, marginBottom:6, fontSize:11}}>
                      <div><span style={{fontWeight:600}}>{tf.from}</span> <span style={{color:C.muted, margin:"0 4px"}}>→</span> <span style={{fontWeight:600}}>{tf.to}</span></div>
                      <div style={{fontWeight:700}}>{fmt(tf.amount)} <button onClick={() => setCustomTransfers(customTransfers.filter((_,idx)=>idx!==i))} style={{background:"transparent", border:"none", color:C.neg, cursor:"pointer", marginLeft:12}}>✕</button></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
		  
{tab === "poolrules" && (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.accent}`, padding: 16, display: "flex", alignItems: "center", gap: 16 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: C.accent, marginBottom: 2 }}>Global OD Pool Minimum</div>
        <div style={{ fontSize: 10, color: C.muted }}>Applies to all pools with an OD facility (converted to local CCY by FX rate). Per-pool override still wins.</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 10, color: C.muted }}>EUR</span>
        <input type="number" value={globalODMin} onChange={e => setGlobalODMin(Number(e.target.value) || 0)}
          style={{ ...F, width: 160, textAlign: "right", fontWeight: 800, color: C.accent, border: `1px solid ${C.accent}` }} />
      </div>
    </div>

    <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.warn}`, padding: 16, display: "flex", alignItems: "center", gap: 16 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: C.warn, marginBottom: 2 }}>Global Funding Top-Up Target</div>
        <div style={{ fontSize: 10, color: C.muted }}>When a pool is funded, fill to this level above zero instead of stopping at zero. 0 = fund to zero (previous behaviour).</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 10, color: C.muted }}>EUR</span>
        <input type="number" value={globalTopUp} onChange={e => setGlobalTopUp(Number(e.target.value) || 0)}
          style={{ ...F, width: 160, textAlign: "right", fontWeight: 800, color: C.warn, border: `1px solid ${C.warn}` }} />
      </div>
    </div>

{["No OD", "OD Pool"].map(group => {
      const isOD = group === "OD Pool";
      const rows = isOD ? odMasterList : poolList.filter(p => !p.hasOD);
      if (!rows.length) return null;
      return (
        <div key={group} style={{ background: C.card, borderRadius: 8, border: `1px solid ${isOD ? C.border : C.accent}`, overflow: "hidden" }}>
          <div style={{ padding: "10px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700,
              background: isOD ? `${C.pos}20` : `${C.accent}20`, color: isOD ? C.pos : C.accent }}>{group}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{isOD ? "Cash Pool Masters — With OD Facility" : "Cash Pools — No Overdraft Facility"}</span>
            <span style={{ fontSize: 10, color: C.muted, marginLeft: "auto" }}>{rows.length} {isOD ? "master" : "pool"}{rows.length !== 1 ? "s" : ""}</span>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead style={{ background: "#ffffff03" }}>
              <tr>
                <th style={{ padding: "8px 16px", textAlign: "left", color: C.muted, fontSize: 9, textTransform: "uppercase", fontWeight: 600 }}>{isOD ? "Master" : "Pool"}</th>
                {!isOD && <th style={{ padding: "8px 8px", textAlign: "left", color: C.muted, fontSize: 9, textTransform: "uppercase", fontWeight: 600 }}>CCY</th>}
                {isOD && <th style={{ padding: "8px 8px", textAlign: "right", color: C.muted, fontSize: 9, textTransform: "uppercase", fontWeight: 600 }}>OD Limit (EUR)</th>}
                <th style={{ padding: "8px 8px", textAlign: "center", color: C.muted, fontSize: 9, textTransform: "uppercase", fontWeight: 600 }}>Sub-Accts</th>
                <th style={{ padding: "8px 8px", textAlign: "right", color: C.muted, fontSize: 9, textTransform: "uppercase", fontWeight: 600 }}>{isOD ? "Auto-Min (EUR)" : "Auto-Min (Local)"}</th>
                <th style={{ padding: "8px 16px", textAlign: "right", color: C.muted, fontSize: 9, textTransform: "uppercase", fontWeight: 600 }}>{isOD ? "Override Floor (EUR)" : "Override Min (Local)"}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, idx) => {
                const key = isOD ? p.key : p.name.toUpperCase().trim();
                const auto = isOD ? globalODMin : p.autoMin;
                const overrideVal = poolMinOverrides[key];
                const hasOverride = overrideVal !== undefined;
                const subs = isOD ? p.subs : p.subs;
                return (
                  <tr key={key} style={{ borderBottom: `1px solid ${C.border}20`, background: idx % 2 === 0 ? "transparent" : "#ffffff01" }}>
                    <td style={{ padding: "10px 16px" }}>
                      <div style={{ fontWeight: 700, color: C.text }}>{isOD ? p.master : p.name}</div>
                      <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{subs.join(", ")}</div>
                    </td>
                    {!isOD && <td style={{ padding: "10px 8px" }}><span style={{ padding: "2px 6px", background: `${C.border}60`, borderRadius: 4, fontSize: 10, color: C.muted, fontWeight: 600 }}>{p.ccy}</span></td>}
                    {isOD && <td style={{ padding: "10px 8px", textAlign: "right", color: C.muted, fontSize: 10 }}>{fmt(p.limit)}</td>}
                    <td style={{ padding: "10px 8px", textAlign: "center", color: C.muted, fontSize: 10 }}>{subs.length}</td>
                    <td style={{ padding: "10px 8px", textAlign: "right", color: hasOverride ? `${C.muted}60` : C.warn, fontSize: 10, fontWeight: hasOverride ? 400 : 600 }}>{fmt(Math.round(auto))}</td>
                    <td style={{ padding: "10px 16px", textAlign: "right" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                        <input type="number" placeholder={fmt(Math.round(auto))} value={hasOverride ? overrideVal : ""}
                          onChange={e => { const v = e.target.value;
                            if (v === "") setPoolMinOverrides(prev => { const c = {...prev}; delete c[key]; return c; });
                            else setPoolMinOverrides(prev => ({ ...prev, [key]: Number(v) })); }}
                          style={{ ...F, width: 140, textAlign: "right", fontWeight: 700, color: hasOverride ? C.accent : C.muted, border: `1px solid ${hasOverride ? C.accent : C.border}` }} />
                        {hasOverride && <button onClick={() => setPoolMinOverrides(prev => { const c = {...prev}; delete c[key]; return c; })} title="Clear override" style={{ background: "none", border: "none", color: C.neg, cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 2px" }}>✕</button>}
                      </div>
                      {hasOverride && <div style={{ fontSize: 9, color: C.pos, marginTop: 3, textAlign: "right" }}>override active</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    })}
  </div>
)}

{tab === "export" && (() => {
  const selectedRows = suggestions.filter(s => selectedSugg.has(suggKey(s)));
  const selSweeps = selectedRows.filter(s => s.cur === s.targetCur);
  const selFx = selectedRows.filter(s => s.cur !== s.targetCur);
  return (
    <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.border}`, padding: 16 }}>
      <h2 style={{ marginTop: 0, marginBottom: 6, fontSize: 15, fontWeight: 800, color: C.text }}>Export Selected Suggestions</h2>
      <p style={{ color: C.muted, fontSize: 12, marginBottom: 14 }}>Only the ticked suggestions from the right panel are included — {selectedRows.length} selected.</p>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <button onClick={() => copyText(buildTransferEmail(selectedRows), "transfers")} style={{ background: C.accent, border: "none", color: "#fff", padding: "8px 14px", borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 11 }}>{copied === "transfers" ? "✓ Copied" : `📋 Copy Transfer Email (${selSweeps.length})`}</button>
        <button onClick={() => copyText(buildFxHedging(selectedRows), "fx")} style={{ background: C.warn, border: "none", color: "#1a1205", padding: "8px 14px", borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 11 }}>{copied === "fx" ? "✓ Copied" : `📋 Copy FX Hedging (${selFx.length})`}</button>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <button onClick={() => exportCsv(selSweeps, "intra_ccy_sweeps.csv")} style={{ background: "#1e293b", border: `1px solid ${C.border}`, color: C.text, padding: "8px 14px", borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 11 }}>⬇ Sweeps CSV ({selSweeps.length})</button>
        <button onClick={() => exportCsv(selFx, "fx_swaps.csv")} style={{ background: "#1e293b", border: `1px solid ${C.border}`, color: C.text, padding: "8px 14px", borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 11 }}>⬇ FX CSV ({selFx.length})</button>
        <button onClick={() => exportCsv(selectedRows, "all_selected.csv")} style={{ background: "#1e293b", border: `1px solid ${C.border}`, color: C.text, padding: "8px 14px", borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 11 }}>⬇ All CSV ({selectedRows.length})</button>
      </div>
      <div style={{ maxHeight: 500, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 6 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
          <thead style={{ position: "sticky", top: 0, background: C.card }}>
            <tr>{["Type","From","To","Amount","Cur","→ Target","Trade","Settle"].map(h => <th key={h} style={{ padding: "6px 8px", textAlign: "left", color: C.muted, fontSize: 9, textTransform: "uppercase", borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {selectedRows.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 20, textAlign: "center", color: C.muted }}>No suggestions ticked.</td></tr>
            ) : selectedRows.map((s, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.border}20` }}>
                <td style={{ padding: "6px 8px", color: s.cur === s.targetCur ? C.accent : C.warn, fontWeight: 700 }}>{s.cur === s.targetCur ? "SWEEP" : "FX"}</td>
                <td style={{ padding: "6px 8px", color: C.text }}>{s.from}</td>
                <td style={{ padding: "6px 8px", color: C.text }}>{s.to}</td>
                <td style={{ padding: "6px 8px", textAlign: "right", color: C.text }}>{fmt(s.amount)}</td>
                <td style={{ padding: "6px 8px", color: C.muted }}>{s.cur}</td>
                <td style={{ padding: "6px 8px", color: C.pos }}>{s.cur === s.targetCur ? "—" : `${fmt(s.targetAmount)} ${s.targetCur}`}</td>
                <td style={{ padding: "6px 8px", color: C.muted }}>{s.date}</td>
                <td style={{ padding: "6px 8px", color: C.muted }}>{s.settleDate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
})()}

{tab === "cashpos" && (() => {
  const col = 0;
  const rows = Object.entries(displayHierarchy)
    .map(([cur, cd]) => ({ cur, eur: getEurVal(cd.totals[col], cur) }))
    .filter(r => Math.abs(r.eur) >= 1)
    .sort((a, b) => b.eur - a.eur);
  const grandEur = rows.reduce((s, r) => s + r.eur, 0);
  const longs = rows.filter(r => r.eur > 0).reduce((s, r) => s + r.eur, 0);
  const shorts = rows.filter(r => r.eur < 0).reduce((s, r) => s + r.eur, 0);
  const maxAbs = Math.max(1, ...rows.map(r => Math.abs(r.eur)));
  const eurM = (v) => { const m = v / 1e6; const a = Math.abs(m); const s = a >= 100 ? a.toFixed(0) : a.toFixed(1); return (v < 0 ? "−" : "") + s + "M"; };

  const Kpi = ({ label, value, color, sub }) => (
    <div style={{ flex: 1, minWidth: 150, background: "#0b1220", border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color, letterSpacing: -0.5, lineHeight: 1, fontFamily: "'DM Sans'" }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: C.muted, marginTop: 6 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: C.text, fontFamily: "'DM Sans'" }}>Group Cash Position</h2>
        <span style={{ fontSize: 11, color: C.muted }}>EUR · as of {data.dates[col] || ""}{applySuggestions ? " · post-optimization" : ""}</span>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Kpi label="Net Position" value={`${eurM(grandEur)} €`} color={grandEur < 0 ? C.neg : C.pos} sub={`${rows.length} currencies`} />
        <Kpi label="Total Long" value={`${eurM(longs)} €`} color={C.pos} sub={`${rows.filter(r => r.eur > 0).length} positive`} />
        <Kpi label="Total Short" value={`${eurM(shorts)} €`} color={C.neg} sub={`${rows.filter(r => r.eur < 0).length} negative`} />
      </div>

      <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 340, background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: "18px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.text, textTransform: "uppercase", letterSpacing: 1 }}>Exposure by Currency</span>
          <div style={{ display: "flex", gap: 14, fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: C.pos }} /> Long</span>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: C.neg }} /> Short</span>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {rows.map(r => {
            const pct = (Math.abs(r.eur) / maxAbs) * 50;
            const pos = r.eur >= 0;
            return (
              <div key={r.cur} style={{ display: "flex", alignItems: "center", height: 26 }}>
                <span style={{ width: 46, fontSize: 11, fontWeight: 700, color: C.text, flexShrink: 0 }}>{r.cur}</span>
                <div style={{ flex: 1, position: "relative", height: 18 }}>
                  <div style={{ position: "absolute", left: "50%", top: -2, bottom: -2, width: 1, background: `${C.border}` }} />
                  <div style={{
                    position: "absolute", top: 2, bottom: 2,
                    [pos ? "left" : "right"]: "50%", width: `${pct}%`,
                    background: pos ? C.pos : C.neg, opacity: 0.9,
                    borderRadius: pos ? "0 4px 4px 0" : "4px 0 0 4px",
                    transition: "width .4s ease"
                  }} />
                </div>
                <span style={{ width: 96, textAlign: "right", fontSize: 11, fontWeight: 700, color: pos ? C.text : C.neg, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{eurM(r.eur)}</span>
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", alignItems: "center", marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
          <span style={{ width: 46, fontSize: 11, fontWeight: 800, color: C.accent, flexShrink: 0 }}>NET</span>
          <div style={{ flex: 1 }} />
          <span style={{ width: 96, textAlign: "right", fontSize: 13, fontWeight: 800, color: grandEur < 0 ? C.neg : C.pos, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{eurM(grandEur)} €</span>
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 340, background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", fontSize: 11, fontWeight: 700, color: C.text, textTransform: "uppercase", letterSpacing: 1, borderBottom: `1px solid ${C.border}` }}>Facility Headroom (EUR)</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr>
              {["Facility", "Balance", "OD Limit", "Headroom", "Utilisation"].map((h, i) => (
                <th key={h} style={{ padding: "10px 20px", textAlign: i === 0 ? "left" : "right", color: C.muted, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.facilityNames.map(fac => {
              const exp = fv(fac, "Expected", col);
              const lim = Math.abs(fv(fac, "Kredit facilitet", col));
              const hr = lim + exp;
              const util = lim > 0 ? Math.max(0, Math.min(1, -Math.min(0, exp) / lim)) : 0;
              const breach = hr < 0;
              return (
                <tr key={fac} style={{ borderTop: `1px solid ${C.border}20` }}>
                  <td style={{ padding: "10px 20px", fontWeight: 700, color: C.text }}>{fac}</td>
                  <td style={{ padding: "10px 20px", textAlign: "right", color: exp < 0 ? C.neg : C.text, fontVariantNumeric: "tabular-nums" }}>{fmt(exp)}</td>
                  <td style={{ padding: "10px 20px", textAlign: "right", color: C.muted, fontVariantNumeric: "tabular-nums" }}>{lim > 0 ? fmt(lim) : "—"}</td>
                  <td style={{ padding: "10px 20px", textAlign: "right", fontWeight: 700, color: breach ? C.neg : C.pos, fontVariantNumeric: "tabular-nums" }}>{fmt(hr)}</td>
                  <td style={{ padding: "10px 20px", textAlign: "right" }}>
                    {lim > 0 ? (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 52, height: 5, background: "#ffffff10", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ width: `${Math.min(100, util * 100)}%`, height: "100%", background: breach ? C.neg : (util > 0.85 ? C.warn : C.pos), borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: 10, color: breach ? C.neg : C.muted, width: 32, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{(util * 100).toFixed(0)}%</span>
                      </div>
                    ) : <span style={{ fontSize: 10, color: C.muted }}>n/a</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
})()}

{tab === "forecast" && (() => {
  const fb = forecastBuckets;
  const B = fb.buckets;
  if (!B.length) return <div style={{ padding: 20, color: C.muted }}>No dates in workbook.</div>;
  const eurM = (v) => { const m = v / 1e6; const a = Math.abs(m); return (v < 0 ? "−" : "") + (a >= 100 ? a.toFixed(0) : a.toFixed(1)) + "M"; };
  const cell = { padding: "7px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums" };
  const hcell = { padding: "8px 10px", textAlign: "right", color: C.muted, fontSize: 9, textTransform: "uppercase", fontWeight: 600, borderBottom: `1px solid ${C.border}` };
  const ccys = Object.keys(B[0].byCcy).sort((a, b) => Math.abs(B[B.length - 1].byCcy[b].closingEur) - Math.abs(B[B.length - 1].byCcy[a].closingEur));
  const br = fb.bridge;
  const steps = [["Opening " + B[0].startDate, br.opening, C.text], ["+ Inflows", br.inflows, C.pos], ["− Outflows", br.outflows, C.neg], ["Closing " + br.closingDate, br.closing, br.closing < 0 ? C.neg : C.accent]];
  const maxAbs = Math.max(1, ...steps.map(s => Math.abs(s[1])));
  const Panel = ({ title, children, note }) => (
    <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.border}`, overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, color: C.text, textTransform: "uppercase", letterSpacing: 1, borderBottom: `1px solid ${C.border}` }}>{title}</div>
      <div style={{ overflowX: "auto" }}>{children}</div>
      {note && <div style={{ padding: "8px 14px", fontSize: 9, color: C.muted, borderTop: `1px solid ${C.border}30` }}>{note}</div>}
    </div>
  );
  const bucketHead = (sub) => (
    <thead><tr>
      <th style={{ ...hcell, textAlign: "left" }}>{sub}</th>
      {B.map(b => <th key={b.label} style={hcell}>{b.label}<div style={{ fontWeight: 400, textTransform: "none" }}>{b.startDate === b.endDate ? b.endDate : `${b.startDate.slice(0, 5)}–${b.endDate.slice(0, 5)}`}</div></th>)}
    </tr></thead>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: C.text, fontFamily: "'DM Sans'" }}>Liquidity Forecast</h2>
        <span style={{ fontSize: 11, color: C.muted }}>EUR · base forecast (excludes simulated optimizations){fb.stressed ? " · STRESSED" : ""}</span>
      </div>

      <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${fb.stressed ? C.warn : C.border}`, padding: "10px 14px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: fb.stressed ? C.warn : C.muted, letterSpacing: 1 }}>{fb.stressed ? "⚠ STRESSED SCENARIO" : "STRESS TEST"}</span>
        {[["inflowHaircut", "Inflow haircut %"], ["outflowShock", "Outflow shock %"]].map(([k, l]) => (
          <label key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: C.muted }}>
            {l}
            <input type="number" min={0} max={100} step={5} value={stress[k]} onChange={e => { const v = Math.max(0, Math.min(k === "inflowHaircut" ? 100 : 1000, Number(e.target.value) || 0)); setStress(s => ({ ...s, [k]: v })); }}
              style={{ ...F, width: 80, textAlign: "right", fontWeight: 700, color: stress[k] ? C.warn : C.text }} />
          </label>
        ))}
        {fb.stressed && <button onClick={() => setStress({ inflowHaircut: 0, outflowShock: 0 })} style={{ background: "none", border: `1px solid ${C.border}`, color: C.muted, padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 10 }}>Reset</button>}
        <span style={{ fontSize: 9, color: C.muted, flex: 1, textAlign: "right" }}>Applies to this tab only: positive Forecast flows × (1 − haircut), negative × (1 + shock), from T+1.</span>
      </div>

      <Panel title={`Cash bridge — group (EUR) ${B[0].startDate} → ${br.closingDate}`} note="Opening = Cash T0. Inflows / outflows = sum of positive / negative daily Forecast flows per account, T+1 onward (T0 forecast is zeroed).">
        <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "14px 18px" }}>
          {steps.map(([l, v, col]) => (
            <div key={l} style={{ display: "flex", alignItems: "center", height: 24 }}>
              <span style={{ width: 170, fontSize: 11, fontWeight: 700, color: C.text }}>{l}</span>
              <div style={{ flex: 1, height: 14, position: "relative" }}>
                <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${(Math.abs(v) / maxAbs) * 100}%`, background: col, opacity: 0.85, borderRadius: 3 }} />
              </div>
              <span style={{ width: 120, textAlign: "right", fontSize: 12, fontWeight: 800, color: col, fontVariantNumeric: "tabular-nums" }}>{fmt(v)}</span>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Group by horizon bucket (EUR)" note="Opening = balance at the end of the previous bucket. Closing = balance on the last date of the bucket. Min = lowest daily group balance in the bucket.">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          {bucketHead("EUR")}
          <tbody>
            {[["Opening", "opening"], ["Inflows", "inflows"], ["Outflows", "outflows"], ["Net flow", "net"], ["Closing", "closing"], ["Min daily", "min"]].map(([l, k]) => (
              <tr key={k} style={{ borderTop: `1px solid ${C.border}20`, background: k === "closing" ? "#1e293b" : "transparent" }}>
                <td style={{ ...cell, textAlign: "left", fontWeight: 700, color: C.text }}>{l}</td>
                {B.map(b => <td key={b.label} style={{ ...cell, fontWeight: k === "closing" ? 800 : 400, color: b.group[k] < 0 ? C.neg : (k === "inflows" ? C.pos : C.text) }}>{fmt(b.group[k])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel title="Currency legs — closing balance at bucket end" note="Local currency and EUR at the FX sheet Mid rate. Red = negative; ⚠ = the leg goes negative on some day inside the bucket.">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
          <thead><tr>
            <th style={{ ...hcell, textAlign: "left" }}>CCY</th>
            {B.map(b => <th key={b.label} colSpan={2} style={{ ...hcell, textAlign: "center" }}>{b.label} · {b.endDate}</th>)}
          </tr><tr>
            <th style={hcell}></th>
            {B.map(b => <React.Fragment key={b.label}><th style={hcell}>Local</th><th style={hcell}>EUR</th></React.Fragment>)}
          </tr></thead>
          <tbody>
            {ccys.map(c => (
              <tr key={c} style={{ borderTop: `1px solid ${C.border}20` }}>
                <td style={{ ...cell, textAlign: "left", fontWeight: 700, color: C.text }}>{c}</td>
                {B.map(b => { const x = b.byCcy[c]; const dip = x.min < 0 && x.closing >= 0; return (
                  <React.Fragment key={b.label}>
                    <td style={{ ...cell, color: x.closing < 0 ? C.neg : C.text }} title={`Opening ${fmt(x.opening)} · In ${fmt(x.inflows)} · Out ${fmt(x.outflows)} · Min ${fmt(x.min)} ${c}`}>{dip ? "⚠ " : ""}{fmt(x.closing)}</td>
                    <td style={{ ...cell, color: x.closingEur < 0 ? C.neg : C.muted }}>{fmt(x.closingEur)}</td>
                  </React.Fragment>
                ); })}
              </tr>
            ))}
            <tr style={{ borderTop: `1px solid ${C.border}`, background: "#1e293b" }}>
              <td style={{ ...cell, textAlign: "left", fontWeight: 800, color: C.accent }}>GROUP</td>
              {B.map(b => <React.Fragment key={b.label}><td style={cell}></td><td style={{ ...cell, fontWeight: 800, color: b.group.closing < 0 ? C.neg : C.pos }}>{fmt(b.group.closing)}</td></React.Fragment>)}
            </tr>
          </tbody>
        </table>
      </Panel>

      <Panel title="Facility headroom by bucket (EUR)" note="Headroom = |Kredit facilitet| + Expected (Σ accounts under the Cash Pool Master). Min = lowest daily headroom within the bucket.">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
          <thead><tr>
            <th style={{ ...hcell, textAlign: "left" }}>Facility</th>
            {B.map(b => <th key={b.label} colSpan={2} style={{ ...hcell, textAlign: "center" }}>{b.label} · {b.endDate}</th>)}
          </tr><tr>
            <th style={hcell}></th>
            {B.map(b => <React.Fragment key={b.label}><th style={hcell}>Headroom</th><th style={hcell}>Min</th></React.Fragment>)}
          </tr></thead>
          <tbody>
            {data.facilityNames.map(f => (
              <tr key={f} style={{ borderTop: `1px solid ${C.border}20` }}>
                <td style={{ ...cell, textAlign: "left", fontWeight: 700, color: C.text, whiteSpace: "nowrap" }}>{f}</td>
                {B.map(b => { const x = b.facilities[f]; return (
                  <React.Fragment key={b.label}>
                    <td style={{ ...cell, fontWeight: 700, color: x.headroom < 0 ? C.neg : C.pos }} title={`Expected ${fmt(x.expected)} · Limit ${fmt(x.limit)}`}>{fmt(x.headroom)}</td>
                    <td style={{ ...cell, color: x.minHeadroom < 0 ? C.neg : C.muted }}>{fmt(x.minHeadroom)}</td>
                  </React.Fragment>
                ); })}
              </tr>
            ))}
            <tr style={{ borderTop: `1px solid ${C.border}`, background: "#1e293b" }}>
              <td style={{ ...cell, textAlign: "left", fontWeight: 800, color: C.accent }}>TOTAL</td>
              {B.map(b => <React.Fragment key={b.label}><td style={{ ...cell, fontWeight: 800, color: b.totalHeadroom < 0 ? C.neg : C.pos }}>{fmt(b.totalHeadroom)}</td><td style={{ ...cell, color: C.muted }}>{fmt(b.totalMinHeadroom)}</td></React.Fragment>)}
            </tr>
          </tbody>
        </table>
      </Panel>
      <Panel title={`Forecast vs actual — actual = Cash on ${accuracy.asOf} · ${fcSnapshots.length} snapshot(s) stored`}
        note="A snapshot of the forecast is stored each time a file is loaded (browser storage, one per as-of date, last 30). Variance = actual − forecast. WAPE = Σ|account variance| ÷ Σ|actual|, EUR at today's FX.">
        <div style={{ padding: "10px 14px", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", borderBottom: `1px solid ${C.border}30` }}>
          <button onClick={() => { const blob = new Blob([JSON.stringify(fcSnapshots)], { type: "application/json" }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "treasury_fc_snapshots.json"; a.click(); URL.revokeObjectURL(url); }}
            style={{ background: "#1e293b", border: `1px solid ${C.border}`, color: C.text, padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 10 }}>⬇ Export snapshots</button>
          <label style={{ background: "#1e293b", border: `1px solid ${C.border}`, color: C.text, padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 10 }}>
            ⬆ Import snapshots<input type="file" accept=".json" style={{ display: "none" }} onChange={e => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = ev => { try { const arr = JSON.parse(ev.target.result); setFcSnapshots(p => mergeSnapshots(p, Array.isArray(arr) ? arr : [arr])); } catch (ex) { setErr(ex.message); } }; r.readAsText(f); e.target.value = ""; }} />
          </label>
          <span style={{ fontSize: 9, color: C.muted }}>Stored as-of dates: {fcSnapshots.map(s => s.asOf).join(", ") || "none"}</span>
        </div>
        {accuracy.comparisons.length === 0 ? (
          <div style={{ padding: 16, fontSize: 11, color: C.muted }}>No prior snapshot covers {accuracy.asOf}. Load a later file (or import snapshots) to see forecast accuracy.</div>
        ) : accuracy.comparisons.map(cmp => (
          <div key={cmp.snapshotAsOf} style={{ borderBottom: `1px solid ${C.border}40` }}>
            <div style={{ padding: "10px 14px", display: "flex", gap: 18, flexWrap: "wrap", fontSize: 11 }}>
              <span style={{ fontWeight: 800, color: C.text }}>Forecast made {cmp.snapshotAsOf} (T+{cmp.horizon})</span>
              <span style={{ color: C.muted }}>Forecast {fmt(cmp.group.forecastEur)} €</span>
              <span style={{ color: C.muted }}>Actual {fmt(cmp.group.actualEur)} €</span>
              <span style={{ fontWeight: 700, color: cmp.group.varianceEur < 0 ? C.neg : C.pos }}>Variance {fmt(cmp.group.varianceEur)} €</span>
              <span style={{ fontWeight: 700, color: C.warn }}>WAPE {cmp.group.wape == null ? "—" : (cmp.group.wape * 100).toFixed(1) + "%"}</span>
              <span style={{ color: C.muted }}>{cmp.accountsWithVariance} acct(s) off · {cmp.notInSnapshot} not in snapshot · {cmp.notInFile} dropped</span>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
              <thead><tr>{["CCY", "Forecast", "Actual", "Variance", "Variance EUR", "|Error| %"].map((h, i) => <th key={h} style={{ ...hcell, textAlign: i === 0 ? "left" : "right" }}>{h}</th>)}</tr></thead>
              <tbody>
                {Object.entries(cmp.byCcy).filter(([, x]) => Math.abs(x.variance) >= 0.005).sort((a, b) => Math.abs(b[1].varianceEur) - Math.abs(a[1].varianceEur)).map(([c, x]) => (
                  <tr key={c} style={{ borderTop: `1px solid ${C.border}20` }}>
                    <td style={{ ...cell, textAlign: "left", fontWeight: 700, color: C.text }}>{c}</td>
                    <td style={cell}>{fmt(x.forecast)}</td>
                    <td style={cell}>{fmt(x.actual)}</td>
                    <td style={{ ...cell, color: x.variance < 0 ? C.neg : C.pos }}>{fmt(x.variance)}</td>
                    <td style={{ ...cell, color: C.muted }}>{fmt(x.varianceEur)}</td>
                    <td style={{ ...cell, color: C.warn }}>{x.absPctError == null ? "—" : (x.absPctError * 100).toFixed(1) + "%"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {cmp.topAccounts.length > 0 && <div style={{ padding: "6px 14px 10px", fontSize: 9, color: C.muted }}>Largest account variances: {cmp.topAccounts.slice(0, 5).map(t => `${t.name} ${fmt(t.variance)} ${t.cur}`).join(" · ")}</div>}
          </div>
        ))}
      </Panel>

      <div style={{ fontSize: 9, color: C.muted }}>Group closing {B[B.length - 1].endDate}: {eurM(B[B.length - 1].group.closing)} € · Total headroom: {eurM(B[B.length - 1].totalHeadroom)} €</div>
    </div>
  );
})()}

{tab === "assumptions" && (() => {
  const constants = [
    ["START_DAY", "1", "buildSuggestions()", "First day index on which transfers are proposed (T+1)."],
    ["SWEEP_MIN", "100,000", "buildSuggestions()", "Minimum intra-currency sweep (EUR equivalent)."],
    ["FX_MIN", "250,000", "buildSuggestions()", "Minimum FX swap (EUR equivalent)."],
    ["FX_LEAD", "3", "buildSuggestions()", "FX trade date = settle date − 3 date columns."],
    ["LOOKAHEAD", "5", "buildSuggestions()", "Days ahead scanned for the worst balance of a leg."],
    ["Default account min", "200,000 EUR × FX", "displayHierarchy · Rules tab · poolList", "Used when no Min Balance / pool override is set."],
    ["Near-limit flag", "15% of OD limit", "Positions → Cash Pool Masters", "Headroom below this is shaded amber."],
    ["Waterfall horizon", "11 dates", "displayHierarchy", "Account-level re-allocation of simulated moves."],
    ["Views horizon", "7 dates (positions) · 12 (heat-map)", "Positions tab · VisualFacilityMonitor", ""]
  ];
  const assumptions = [
    ["D1", "Facility Opening / Net movements / Expected are derived from the account balances (OD_Struktur 'Cash Pool Master'); only 'Kredit facilitet' is read from 'Total overview'."],
    ["D2", "Forecast T0 is zeroed: balance = Cash[T0] + cumulative Forecast from T+1."],
    ["D3", "OD_Facilities and Facilities sheets are not read."],
    ["D6", "Horizon buckets T0 / T+1 / T+2–5 / T+6–11 are date-column positions (business days in the workbook)."],
    ["FX", "FX sheet 'Mid' = units of currency per 1 EUR; EUR = local ÷ Mid. A missing rate converts at 1.0 (flagged on Data Quality)."],
    ["Flows", "Inflows / outflows = sign of each account's daily Forecast flow (no flow category in the workbook)."],
    ["Limit", "'Kredit facilitet' is constant over the horizon; blank dates are forward-filled."]
  ];
  const settings = [
    ["globalODMin", `${fmt(globalODMin)} EUR`, "Pool Min. Rules"],
    ["globalTopUp", `${fmt(globalTopUp)} EUR`, "Pool Min. Rules"],
    ["poolMinOverrides", `${Object.keys(poolMinOverrides).length} override(s)`, "Pool Min. Rules"],
    ["minBalances", `${Object.values(minBalances).filter(v => v !== "" && v !== undefined).length} account(s)`, "Rules Configuration"],
    ["odAccess", `${Object.values(odAccess).filter(Boolean).length} account(s) ticked`, "Rules Configuration"],
    ["ccyPriority", ccyPriority.join(" > "), "FX Source Priority"],
    ["intercompanyLines", `${Object.keys(intercompanyLines).length} line(s)`, "localStorage only (no UI)"]
  ];
  const download = (content, name, type) => { const blob = new Blob([content], { type }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url); };
  const esc = v => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const hc = { padding: "8px 12px", textAlign: "left", color: C.muted, fontSize: 9, textTransform: "uppercase", fontWeight: 600, borderBottom: `1px solid ${C.border}` };
  const td = { padding: "7px 12px", fontSize: 10, color: C.text, borderTop: `1px solid ${C.border}20`, verticalAlign: "top" };
  const Panel = ({ title, right, children }) => (
    <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.border}`, overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: C.text, textTransform: "uppercase", letterSpacing: 1, flex: 1 }}>{title}</span>{right}
      </div>
      <div style={{ overflowX: "auto" }}>{children}</div>
    </div>
  );
  const btn = { background: "#1e293b", border: `1px solid ${C.border}`, color: C.text, padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 10 };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Panel title="Model assumptions">
        <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>
          {assumptions.map(([k, v]) => <tr key={k}><td style={{ ...td, width: 60, fontWeight: 800, color: C.accent }}>{k}</td><td style={td}>Assumption: {v}</td></tr>)}
        </tbody></table>
      </Panel>
      <Panel title="Hard-coded parameters (display only)">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Parameter", "Value", "Where", "Meaning"].map(h => <th key={h} style={hc}>{h}</th>)}</tr></thead>
          <tbody>{constants.map(c => <tr key={c[0]}>{c.map((v, i) => <td key={i} style={{ ...td, fontWeight: i === 0 ? 700 : 400, color: i === 1 ? C.warn : (i === 2 ? C.muted : C.text) }}>{v}</td>)}</tr>)}</tbody>
        </table>
      </Panel>
      <Panel title="Current user settings (localStorage)">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Setting", "Value", "Edited in"].map(h => <th key={h} style={hc}>{h}</th>)}</tr></thead>
          <tbody>{settings.map(s => <tr key={s[0]}>{s.map((v, i) => <td key={i} style={{ ...td, fontWeight: i === 0 ? 700 : 400, color: i === 2 ? C.muted : C.text }}>{v}</td>)}</tr>)}</tbody>
        </table>
      </Panel>
      <Panel title={`Audit trail (${auditLog.length})`} right={<>
        <button style={btn} onClick={() => download(JSON.stringify({ exportedAt: new Date().toISOString(), file: data.fileInfo || null, asOf: data.dates[0], assumptions, constants, settings: auditSettings, auditLog }, null, 2), "treasury_audit.json", "application/json")}>⬇ JSON</button>
        <button style={btn} onClick={() => download([["Timestamp", "Setting", "From", "To"].join(",")].concat(auditLog.map(e => [e.ts, e.setting, e.from, e.to].map(esc).join(","))).join("\n"), "treasury_audit_log.csv", "text/csv;charset=utf-8;")}>⬇ CSV</button>
      </>}>
        <div style={{ maxHeight: 320, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ position: "sticky", top: 0, background: C.card }}><tr>{["Time", "Setting", "From", "To"].map(h => <th key={h} style={hc}>{h}</th>)}</tr></thead>
            <tbody>
              {auditLog.length === 0 ? <tr><td colSpan={4} style={{ ...td, color: C.muted, textAlign: "center" }}>No changes recorded yet.</td></tr>
                : [...auditLog].reverse().map((e, i) => (
                  <tr key={i}>
                    <td style={{ ...td, color: C.muted, whiteSpace: "nowrap" }}>{new Date(e.ts).toLocaleString("en-GB")}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{e.setting}</td>
                    <td style={{ ...td, color: C.muted, maxWidth: 260, wordBreak: "break-all" }}>{e.from}</td>
                    <td style={{ ...td, maxWidth: 260, wordBreak: "break-all" }}>{e.to}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
})()}

{tab === "dataquality" && (() => {
  const dg = data.diagnostics || {};
  const fi = data.fileInfo || {};
  const ts = v => v ? new Date(v).toLocaleString("en-GB") : "—";
  const ageH = fi.lastModified ? (Date.now() - fi.lastModified) / 3600000 : null;
  const coercedTotal = Object.values(dg.coerced || {}).reduce((s, c) => s + c.count, 0);
  const sd = dg.sheetDates || {};
  const datesOk = ["Cash", "Forecast"].every(s => sd[s] === sd["Net"]);
  const toOk = (dg.toDates || [])[0] === data.dates[0] && (dg.toDates || []).length === data.dates.length;
  const di = Math.min(dqDate, data.dates.length - 1);
  const Badge = ({ ok, label }) => <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 9, fontWeight: 800, background: ok ? `${C.pos}20` : `${C.warn}20`, color: ok ? C.pos : C.warn }}>{label || (ok ? "OK" : "CHECK")}</span>;
  const Check = ({ ok, title, detail, label }) => (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 14px", borderBottom: `1px solid ${C.border}30` }}>
      <div style={{ width: 70, flexShrink: 0 }}><Badge ok={ok} label={label} /></div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.text }}>{title}</div>
        {detail && <div style={{ fontSize: 10, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>{detail}</div>}
      </div>
    </div>
  );
  const Card = ({ label, value, sub }) => (
    <div style={{ flex: 1, minWidth: 170, background: "#0b1220", border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
      <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 800, color: C.text, wordBreak: "break-all" }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Card label="Source file" value={fi.name || "—"} sub={`Modified ${ts(fi.lastModified)}${ageH != null ? ` · ${ageH.toFixed(1)} h old` : ""}`} />
        <Card label="As of → horizon" value={`${dg.asOf} → ${dg.horizonEnd}`} sub={`${data.dates.length} dates · loaded ${ts(fi.loadedAt)}`} />
        <Card label="Scope" value={`${data.netData.length} accounts · ${new Set(data.netData.map(a => a.cur)).size} ccys`} sub={`${data.facilityNames.length} facilities`} />
        <Card label="FX rates" value={`${Object.keys(data.fxRates).length} rates (FX sheet)`} sub="Rate date: not in workbook" />
      </div>

      <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, color: C.text, textTransform: "uppercase", letterSpacing: 1, borderBottom: `1px solid ${C.border}` }}>Data checks</div>
        <Check ok={datesOk} title={`Date columns — Net ${sd["Net"] ?? "—"} · Cash ${sd["Cash"] ?? "—"} · Forecast ${sd["Forecast"] ?? "—"}`} detail="Cash and Forecast must carry the same date columns as Net." />
        <Check ok={toOk} label={toOk ? "OK" : "INFO"} title={`Total overview dates: ${(dg.toDates || [])[0] || "—"} → ${(dg.toDates || []).slice(-1)[0] || "—"} (${(dg.toDates || []).length})`} detail="Only 'Kredit facilitet' (limit) is read from this sheet. Opening / Net movements / Expected are derived from the account balances below." />
        <Check ok={coercedTotal === 0} title={`${coercedTotal} cell(s) read as 0 because they held text / errors`} detail={Object.entries(dg.coerced || {}).map(([s, c]) => `${s}: ${c.count} (e.g. ${c.samples.join(", ")})`).join(" · ") || "None."} />
        <Check ok={!(dg.missingFx || []).length} title={`${(dg.missingFx || []).length} currency(ies) without FX rate`} detail={(dg.missingFx || []).length ? `Converted at 1.0: ${dg.missingFx.join(", ")}` : "Every account currency has a Mid rate."} />
        <Check ok={!(dg.unmapped || []).length} title={`${(dg.unmapped || []).length} Net account(s) missing from OD_Struktur`} detail={(dg.unmapped || []).join(", ") || "None."} />
        <Check ok={!(dg.strukturNotInNet || []).length} title={`${(dg.strukturNotInNet || []).length} OD_Struktur Subaccount(s) not in Net`} detail={(dg.strukturNotInNet || []).join(", ") || "None."} />
        <Check ok={!(dg.t0Excluded || []).length} label={(dg.t0Excluded || []).length ? "INFO" : "OK"} title={`${(dg.t0Excluded || []).length} account(s) with a T0 Forecast flow excluded (T0 forecast is zeroed)`}
          detail={(dg.t0Excluded || []).map(t => `${t.name} ${fmt(t.value)} ${t.cur}`).join(" · ") || "None."} />
      </div>

      <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.text, textTransform: "uppercase", letterSpacing: 1, flex: 1 }}>Reconciliation (EUR)</span>
          <select value={di} onChange={e => setDqDate(Number(e.target.value))} style={{ ...F, width: 140 }}>
            {data.dates.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
          <thead>
            <tr>{["Facility", "Σ Accounts (app)", "Facility Expected", "Diff", "Net sheet", "T0 fcst excluded", "Unexplained", ""].map((h, i) => (
              <th key={i} style={{ padding: "8px 12px", textAlign: i === 0 ? "left" : "right", color: C.muted, fontSize: 9, textTransform: "uppercase", fontWeight: 600 }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {reconciliation.rows.map(r => {
              const x = r.byDate[di];
              const ok = (x.diffAcctFac == null || Math.abs(x.diffAcctFac) < 0.01) && Math.abs(x.unexplained) < 0.01;
              return (
                <tr key={r.name} style={{ borderTop: `1px solid ${C.border}20`, background: r.name === "GROUP" ? "#1e293b" : "transparent" }}>
                  <td style={{ padding: "8px 12px", fontWeight: 700, color: C.text }}>{r.name === "GROUP" ? "GROUP (all accounts)" : r.name}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right" }}>{fmt(x.accountsEur)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right" }}>{x.facilityEur == null ? "—" : fmt(x.facilityEur)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", color: x.diffAcctFac && Math.abs(x.diffAcctFac) >= 0.01 ? C.neg : C.muted }}>{x.diffAcctFac == null ? "—" : fmt(x.diffAcctFac)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right" }}>{fmt(x.netSheetEur)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", color: C.muted }}>{fmt(x.t0ExcludedEur)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", color: Math.abs(x.unexplained) >= 0.01 ? C.neg : C.muted }}>{fmt(x.unexplained)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right" }}><Badge ok={ok} label={ok ? "TIES" : "BREAK"} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ padding: "8px 14px", fontSize: 9, color: C.muted, borderTop: `1px solid ${C.border}30` }}>Net sheet − Σ Accounts − T0 forecast excluded = Unexplained. Facility Expected is derived from Σ Accounts, so Diff must be 0.</div>
      </div>
    </div>
  );
})()}

{tab === "fxpriority" && (
  <div style={{background:C.card, borderRadius:8, border:`1px solid ${C.border}`, padding:16}}>
    <h2 style={{marginTop:0, marginBottom:6, fontSize:15, fontWeight:800, color:C.text}}>FX Source Currency Priority</h2>
    <p style={{color:C.muted, fontSize:12, marginBottom:14}}>When funding shortfalls via FX swaps, currencies higher in this list are drawn from first. EUR at top = always preferred source.</p>
    <div style={{maxWidth: 400, border:`1px solid ${C.border}`, borderRadius:6, overflow:"hidden"}}>
      {ccyPriority.map((c, i) => (
        <div key={c} style={{display:"flex", alignItems:"center", padding:"10px 14px", background: i % 2 === 0 ? "transparent" : "#ffffff01", borderBottom: i === ccyPriority.length - 1 ? "none" : `1px solid ${C.border}30`}}>
          <span style={{fontSize:11, color:C.accent, fontWeight:800, width:28}}>#{i+1}</span>
          <span style={{flex:1, fontWeight:700, color:C.text}}>{c}</span>
          <button disabled={i===0} onClick={() => setCcyPriority(p => { const n = [...p]; [n[i-1], n[i]] = [n[i], n[i-1]]; return n; })}
            style={{padding:"4px 8px", background:C.card, border:`1px solid ${C.border}`, borderRadius:4, color:i===0?C.border:C.text, cursor:i===0?"not-allowed":"pointer", marginRight:4, fontSize:11}}>▲</button>
          <button disabled={i===ccyPriority.length-1} onClick={() => setCcyPriority(p => { const n = [...p]; [n[i+1], n[i]] = [n[i], n[i+1]]; return n; })}
            style={{padding:"4px 8px", background:C.card, border:`1px solid ${C.border}`, borderRadius:4, color:i===ccyPriority.length-1?C.border:C.text, cursor:i===ccyPriority.length-1?"not-allowed":"pointer", fontSize:11}}>▼</button>
        </div>
      ))}
    </div>
  </div>
)}

{tab === "cashpools" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 2 }}>
                {Object.keys(cashPoolStructure).sort().map(bank => {
                  const isExp = expBank.has(bank);
                  const count = Object.values(cashPoolStructure[bank]).reduce((a, m) => a + Object.keys(m.pools).length, 0);
                  return (
                    <button key={bank} onClick={() => setExpBank(p => { const n = new Set(p); isExp ? n.delete(bank) : n.add(bank); return n; })} style={{ background: isExp ? C.accent : C.card, border: `1px solid ${isExp ? C.accent : C.border}`, color: isExp ? "#fff" : C.text, padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontFamily: "'DM Sans'", fontWeight: 700, fontSize: 12, display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <span>{bank}</span>
                      <span style={{ fontSize: 9, opacity: 0.6, fontWeight: 400 }}>{count} pool{count !== 1 ? "s" : ""}</span>
                    </button>
                  );
                })}
              </div>
              {Object.keys(cashPoolStructure).sort().filter(b => expBank.has(b)).map(bank => (
                <div key={bank} style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.accent}`, padding: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.accent, marginBottom: 16 }}>{bank} — Structure Map</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                    {Object.values(cashPoolStructure[bank]).map(master => {
                      const pools = Object.values(master.pools);
                      return (
                        <div key={master.name} style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "4px 0" }}>
                          <div style={{ background: `linear-gradient(135deg, ${C.accent} 0%, #0ea5e9 100%)`, color: "#fff", fontWeight: 800, fontSize: 12, padding: "8px 20px", borderRadius: 8, boxShadow: `0 2px 10px ${C.accent}30` }}>{master.name}</div>
                          <div style={{ width: 2, height: 16, background: C.border }} />
                          <div style={{ position: "relative", display: "flex", gap: 32, justifyContent: "center", flexWrap: "wrap", paddingTop: pools.length > 1 ? 12 : 0 }}>
                            {pools.length > 1 && <div style={{ position: "absolute", top: 0, left: "10%", right: "10%", height: 1, background: C.border }} />}
                            {pools.map(pool => (
                              <div key={pool.name} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                {pools.length > 1 && <div style={{ width: 2, height: 12, background: C.border, marginBottom: -1 }} />}
                                <PoolTreeNode name={pool.name} ccy={pool.ccy} subs={pool.subs} />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
           </div>
          )}
        </div>
		
        {/* ── RIGHT PANEL: ACTIONABLE SIDEBAR ── */}
<div style={{ width: "clamp(360px, 26vw, 460px)", flexShrink: 0, display: "flex", flexDirection: "column", gap: 14, paddingRight: 4, position: "sticky", top: 12, alignSelf: "flex-start", maxHeight: "calc(100vh - 24px)", overflowY: "auto" }}>
          <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.border}`, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: C.accent, letterSpacing: 0.5 }}>FRAMEWORK PIPELINE CONTROLS</div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, background: "#ffffff01", padding: "8px 10px", borderRadius: 6, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: 11, fontWeight: 600, color: showForecast ? C.pos : C.muted }}>
              <input type="checkbox" checked={showForecast} onChange={(e) => setShowForecast(e.target.checked)} style={{ cursor: "pointer" }} />
              ⚡ Include Forecast Pipeline Flow
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, background: "#ffffff01", padding: "8px 10px", borderRadius: 6, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: 11, fontWeight: 600, color: applySuggestions ? C.accent : C.muted }}>
              <input type="checkbox" checked={applySuggestions} onChange={(e) => setApplySuggestions(e.target.checked)} style={{ cursor: "pointer" }} />
              ✦ Simulate Optimizations in Views
            </label>
          </div>
          <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.border}`, padding: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>Facility Risk Monitor</span>
              <span style={{ fontSize: 8, background: activeBreaches.length ? `${C.neg}20` : `${C.pos}20`, color: activeBreaches.length ? C.neg : C.pos, padding: "1px 6px", borderRadius: 8, fontWeight: 700 }}>{activeBreaches.length ? `${activeBreaches.length} Breach` : "Compliant"}</span>
            </div>
            {activeBreaches.length === 0 ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", background: `${C.pos}03`, border: `1px solid ${C.pos}10`, borderRadius: 4 }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.pos }} />
                <span style={{ fontSize: 10, color: C.pos }}>All Master Facilities protected.</span>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {activeBreaches.map(b => (
                  <div key={b.fac} style={{ padding: 6, background: `${C.neg}05`, border: `1px solid ${C.neg}15`, borderRadius: 4, display: "flex", justifyContent: "space-between", fontSize: 10 }}>
                    <span style={{ fontWeight: 600 }}>{b.fac}</span>
                    <span style={{ color: C.neg, fontWeight: 700 }}>{fmt(b.headroom)} EUR</span>
                  </div>
                ))}
              </div>
            )}
          </div>

{selectedSugg.size > 0 && (
            <div style={{ background: `${C.accent}10`, border: `1px solid ${C.accent}`, borderRadius: 6, padding: "6px 10px", fontSize: 10, fontWeight: 700, color: C.accent, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>✓ {selectedSugg.size} selected for execution</span>
              <button onClick={() => setSelectedSugg(new Set())} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 10 }}>clear</button>
            </div>
          )}

          <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.border}`, padding: 12, display: "flex", flexDirection: "column" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.text, textTransform: "uppercase", marginBottom: 8, borderBottom: `1px solid ${C.border}`, paddingBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
<span onClick={() => setShowSweeps(s => !s)} style={{ cursor: "pointer" }}>{showSweeps ? "▼" : "▶"} 🔁 Intra-CCY Sweeps</span>
                <button onClick={handleRefreshSuggestions} style={{ padding: "4px 8px", background: `${C.accent}20`, color: C.accent, border: `1px solid ${C.accent}50`, borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700 }}>🔄</button>
              </div>
              <span style={{ color: C.accent }}>{sweeps.length}</span>
            </div>
{showSweeps && <div style={{ flex: 1, overflowY: "auto", paddingRight: 2 }}>
              <SuggFeed items={sweeps} dates={data.dates} selected={selectedSugg} toggle={toggleSugg} suggKey={suggKey} emptyMsg="✅ No sweeps required." />
            </div>}
          </div>

          <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.border}`, padding: 12, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.text, textTransform: "uppercase", marginBottom: 8, borderBottom: `1px solid ${C.border}`, paddingBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
<span onClick={() => setShowFx(s => !s)} style={{ cursor: "pointer" }}>{showFx ? "▼" : "▶"} 💱 FX Swaps (T+3)</span>
              <span style={{ color: C.warn }}>{fxTrades.length}</span>
            </div>
            {showFx && <div style={{ paddingRight: 2 }}>
              <SuggFeed items={fxTrades} dates={data.dates} selected={selectedSugg} toggle={toggleSugg} suggKey={suggKey} emptyMsg="✅ No FX trades required." />
            </div>}
          </div>

          
        </div>
      </div>
    </div>
  );
}